<template>
  <el-card :key="facility" v-loading="loading" shadow="never">
    <div slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
        <el-breadcrumb-item v-if="$root.state.facility" :to="{ name: 'Facility Sitemap' }">
          {{ String($root.state.facilityData.code).toUpperCase() }} Site Plan
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <!-- Three.js Canvas + Overlay Controls -->
    <div ref="sceneContainer" class="scene-container" @contextmenu.prevent>
      <canvas ref="canvas"></canvas>

      <!-- Import Status Banner (inside canvas) -->
      <el-alert
        v-if="importStatus === 'queued' || importStatus === 'processing'"
        title="Importing DXF..."
        type="info"
        :closable="false"
        show-icon
        class="import-banner"
      >
        <span>Your CAD file is being processed. This may take a minute.</span>
      </el-alert>
      <el-alert
        v-if="importStatus === 'failed'"
        :title="`Import failed: ${importError || 'Unknown error'}`"
        type="error"
        show-icon
        class="import-banner"
      />

      <!-- In-canvas toast notifications -->
      <transition-group name="canvas-toast" tag="div" class="canvas-toast-container">
        <div v-for="toast in canvasToasts" :key="toast.id" :class="['canvas-toast', 'canvas-toast--' + toast.type]">
          <i :class="toast.icon" />
          <span>{{ toast.message }}</span>
        </div>
      </transition-group>

      <div class="scene-overlay">
        <!-- Top-left controls -->
        <div class="scene-overlay-top">
          <div class="scene-overlay-stack">
            <div class="scene-overlay-panel">
              <el-switch
                v-model="enableLeaseStatus"
                active-text="Finance Status"
                inactive-text="Leasing Status"
                @change="updateUnitColors"
              />
            </div>
            <div class="scene-overlay-panel">
              <el-switch
                v-model="enableParking"
                active-text="View all"
                inactive-text="Disable Parking"
                @change="getUnits"
              />
            </div>
            <div class="scene-overlay-panel">
              <el-button size="mini" class="scene-btn" :class="{ 'is-active': activeTool === 'pan' }" icon="el-icon-rank" @click="setTool('pan')">Move</el-button>
              <el-button size="mini" class="scene-btn" :class="{ 'is-active': activeTool === 'select' }" @click="setTool('select')"><svg class="scene-btn-icon" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><rect x="1.5" y="1.5" width="13" height="13" rx="1" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="3 2"/></svg> Select</el-button>
            </div>
            <div v-if="isEditMode" class="scene-overlay-panel">
              <span class="scene-toolbar-label">Draw:</span>
              <el-button size="mini" class="scene-btn" :class="{ 'is-active': activeTool === 'rect' }" icon="el-icon-crop" @click="setTool('rect')">Rect</el-button>
              <el-button size="mini" class="scene-btn" :class="{ 'is-active': activeTool === 'polygon' }" icon="el-icon-edit-outline" @click="setTool('polygon')">Polygon</el-button>
            </div>
            <div v-if="isEditMode" class="scene-overlay-panel">
              <span class="scene-toolbar-label">Viewport:</span>
              <el-button size="mini" class="scene-btn" icon="el-icon-view" :loading="viewportSaving" @click="saveDefaultViewport">Set Default</el-button>
              <el-button size="mini" class="scene-btn" icon="el-icon-aim" @click="fitEntireSite">Fit All</el-button>
              <el-button size="mini" class="scene-btn is-danger" icon="el-icon-delete" :loading="viewportSaving" :disabled="!facilityData || !facilityData.sitePlanViewport" @click="clearDefaultViewport">Clear</el-button>
            </div>
            <div v-if="!isIOS" class="scene-overlay-panel scene-overlay-help">
              <button type="button" class="scene-help-toggle" @click="isSceneHelpCollapsed = !isSceneHelpCollapsed">
                <i class="el-icon-info" style="margin-right: 4px;" />
                <span>Controls</span>
                <i class="el-icon-arrow-down scene-help-chevron" :class="{ 'is-collapsed': isSceneHelpCollapsed }" />
              </button>
              <div v-show="!isSceneHelpCollapsed" class="scene-help-content">
                <div class="scene-help-item"><i class="el-icon-zoom-in" /> Scroll wheel or trackpad pinch to zoom.</div>
                <div class="scene-help-item"><i class="el-icon-rank" /> On touch, drag with one finger to select. Two fingers to pan &amp; zoom.</div>
              </div>
            </div>

            <div v-if="isEditMode && selectedUnitIds.length > 0" class="scene-overlay-panel">
              <el-button size="mini" class="scene-btn" :disabled="!canCopy" icon="el-icon-document-copy" @click="copySelectedGeometry">Duplicate</el-button>
              <el-button size="mini" class="scene-btn" :disabled="!canRename" icon="el-icon-edit" @click="openRenameDialog">Reassign</el-button>
              <el-button size="mini" class="scene-btn is-danger" :disabled="!canDelete" icon="el-icon-delete" @click="deleteSelectedSitePlans">Delete</el-button>
            </div>
          </div>

          <!-- Top-right: PDF, Legend, Edit, Parking, DXF import -->
          <div class="scene-overlay-stack scene-overlay-stack--right">
            <div v-if="supportsFullscreen || hasAccess" class="scene-overlay-panel">
              <el-button v-if="supportsFullscreen" icon="el-icon-download" size="mini" class="scene-btn" :loading="pdfLoading" @click="saveToPdf">
                Save to PDF
              </el-button>
              <el-button v-if="supportsFullscreen" size="mini" class="scene-btn" :class="{ 'is-active': isFullscreen }" icon="el-icon-full-screen" @click="toggleFullscreen">
                {{ isFullscreen ? 'Exit Fullscreen' : 'Fullscreen' }}
              </el-button>

              <template v-if="hasAccess">
                <el-button v-if="!isEditMode" size="mini" class="scene-btn" icon="el-icon-edit" @click="enterEditMode">Edit</el-button>
                <template v-else>
                  <el-button size="mini" class="scene-btn" @click="cancelEditMode">Cancel</el-button>
                  <el-button size="mini" class="scene-btn is-active" @click="saveEditMode">Save</el-button>
                </template>
              </template>
            </div>
            <div class="scene-overlay-panel legend-popover-wrap">
              <el-button size="mini" class="scene-btn" icon="el-icon-question" @mouseenter.native="legendPopoverVisible = true" @mouseleave.native="legendPopoverVisible = false">Legend</el-button>
              <el-upload
                v-if="isEditMode && hasImportAccess"
                class="dxf-upload"
                name="document"
                :action="uploadApi"
                :headers="uploadHeaders"
                accept=".dxf"
                :show-file-list="false"
                :on-success="handleDxfUpload"
                :before-upload="beforeDxfUpload"
              >
                <el-button size="mini" class="scene-btn" icon="el-icon-upload2" :loading="importing || importStatus === 'queued' || importStatus === 'processing'">
                  {{ importButtonText }}
                </el-button>
              </el-upload>
              <div v-show="legendPopoverVisible" class="legend-popover">
                <LegendSiteMap
                  v-if="org && facilityData"
                  :facility-data="facilityData"
                  :org="org"
                  :parkingPricing="facilityData.parkingPricing || {}"
                  :leaseStatus="enableLeaseStatus"
                  :key="enableLeaseStatus"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Polygon help bar -->
        <div v-if="activeTool === 'polygon' && polygonPoints.length" class="scene-help-bar">
          <span>Click to add points. Double-click or press Enter to finish. Esc to cancel.</span>
          <el-button size="mini" class="scene-btn is-active" :disabled="polygonPoints.length < 3" @click="finishPolygonDraft">Done</el-button>
          <el-button size="mini" class="scene-btn" @click="cancelInteraction">Cancel</el-button>
        </div>

        <!-- Single unit info panel -->
        <transition name="el-fade-in">
          <div v-if="selectedUnit && selectedUnitIds.length === 1 && activeTool === 'select' && !selectionRectStart && !isEditMode" class="scene-side-panel" :style="unitPanelStyle">
            <div class="side-panel-header">
              <span class="unit-info-name">{{ selectedUnit.name }}</span>
              <el-button type="text" icon="el-icon-close" class="side-panel-close" @click="clearSelection" />
            </div>
            <div class="unit-info-body">
              <div class="info-row">
                <span class="info-label">Status</span>
                <span>{{ selectedUnit.status | printableStatus }}</span>
              </div>
              <div class="info-row" v-if="selectedUnit.leaseStatus && selectedUnit.status !== 'AVAILABLE' && selectedUnit.status !== 'UNAVAILABLE'">
                <span class="info-label">Finance</span>
                <span>{{ selectedUnit.leaseStatus | printableStatus }}</span>
              </div>
              <div class="info-row" v-if="selectedUnit.status !== 'AVAILABLE'">
                <span class="info-label">Occupant</span>
                <span v-if="loadingCustomers">Loading...</span>
                <span v-else-if=" customers && selectedUnit.rental && selectedUnit.rental.customer && customers[selectedUnit.rental.customer]">{{ customers[selectedUnit.rental.customer].company }}</span>
              </div>
              <div class="info-row" v-if="selectedUnit.rental && selectedUnit.rental.moveOut">
                <span class="info-label">Move out</span>
                <span>{{ selectedUnit.rental.moveOut | formatDate }}</span>
              </div>
              <div class="info-row" v-if="selectedUnit.computedSize">
                <span class="info-label">Size</span>
                <span>{{ selectedUnit.computedSize }} Sq. ft.</span>
              </div>
              <div class="info-row">
                <span class="info-label">Asking Price</span>
                <span>
                  {{ selectedUnitPrice | currencySign($root.state.facilityData) }}
                  <span v-if="securityFeeEnabled">
                    + {{ computeSecurityFee(selectedUnit.computedSize) | currencySign($root.state.facilityData) }}
                  </span>
                </span>
              </div>
              <div class="info-row" v-if="selectedUnit.tags && selectedUnit.tags.length">
                <span class="info-label">Tags</span>
                <div class="unit-tags">
                  <el-tag size="mini" :key="tag.value" v-for="tag in selectedUnit.tags">{{ tag.value }}</el-tag>
                </div>
              </div>
              <div class="info-row" v-if="selectedUnit.description">
                <span class="info-label">Description</span>
                <span class="info-value--wrap">{{ truncateText(selectedUnit.description, 120) }}</span>
              </div>
              <div class="info-row" v-if="selectedUnit.status === 'PENDING'">
                <span class="info-label">Pending</span>
                <span>{{ getPendingItemsSummary(selectedUnit) }}</span>
              </div>
              <el-button size="small" class="scene-btn is-active" style="margin-top: 8px; width: 100%;" @click="navigateToUnit">
                View Unit Details
              </el-button>
            </div>
          </div>
        </transition>

        <!-- Multi-unit selection label -->
        <transition name="el-fade-in">
          <div v-if="selectedUnitIds.length > 1 && activeTool === 'select' && !selectionRectStart" class="scene-side-panel">
            <div class="side-panel-header">
              <span class="unit-info-name">{{ selectedUnitIds.length }} units selected</span>
              <el-button type="text" icon="el-icon-close" class="side-panel-close" @click="clearSelection" />
            </div>
          </div>
        </transition>

        <!-- Bottom-right: Center View -->
        <transition name="el-fade-in">
          <div v-if="!isViewCentered" class="scene-overlay-bottom-right">
            <div class="scene-overlay-panel">
              <el-button size="mini" class="scene-btn" icon="el-icon-aim" @click="centerView">Center View</el-button>
            </div>
          </div>
        </transition>

      </div>

      <!-- Assign Unit Dialog -->
      <el-dialog title="Assign unit" :visible.sync="assignDialogVisible" width="420px" :close-on-click-modal="false" :append-to-body="false" :modal-append-to-body="false">
        <el-alert
          title="To create a new unit, visit the Units tab."
          type="info"
          :closable="false"
          show-icon
          style="margin-bottom: 16px;"
        />
        <el-form label-position="left" label-width="90px">
          <el-form-item label="Select Unit:">
            <el-select v-model="assignForm.unitId" filterable placeholder="Select a unit" style="width: 100%;" :popper-append-to-body="false">
              <el-option
                v-for="unit in assignableUnits"
                :key="unit._id"
                :label="unit.name"
                :value="unit._id"
              />
            </el-select>
          </el-form-item>
        </el-form>
        <span slot="footer">
          <el-button @click="closeAssignDialog">Cancel</el-button>
          <el-button type="primary" :loading="savingSitePlan" :disabled="!assignForm.unitId" @click="confirmAssignDialog">Assign</el-button>
        </span>
      </el-dialog>

      <!-- Rename Dialog -->
      <el-dialog title="Reassign unit" :visible.sync="renameDialogVisible" width="420px" :close-on-click-modal="false" :append-to-body="false" :modal-append-to-body="false">
        <el-form label-position="top" @submit.native.prevent>
          <el-form-item label="Reassign to unit">
            <el-select v-model="renameForm.unitId" filterable placeholder="Select a unit" style="width: 100%;" :popper-append-to-body="false">
              <el-option
                v-for="unit in renameAssignableUnits"
                :key="unit._id"
                :label="unit.name"
                :value="unit._id"
              />
            </el-select>
          </el-form-item>
        </el-form>
        <span slot="footer">
          <el-button @click="renameDialogVisible = false">Cancel</el-button>
          <el-button type="primary" :loading="savingSitePlan" :disabled="!renameForm.unitId" @click="confirmRenameDialog">Save</el-button>
        </span>
      </el-dialog>

      <!-- Delete Confirm Dialog -->
      <el-dialog title="Confirm Delete" :visible.sync="deleteConfirmVisible" width="420px" :close-on-click-modal="false" :append-to-body="false" :modal-append-to-body="false">
        <span><i class="el-icon-warning" style="color: #e6a23c; margin-right: 8px;" />{{ deleteConfirmMessage }}</span>
        <span slot="footer">
          <el-button @click="deleteConfirmVisible = false">Cancel</el-button>
          <el-button type="primary" @click="executeDeleteSitePlans">OK</el-button>
        </span>
      </el-dialog>
    </div>
  </el-card>
</template>

<script>
import axios from 'axios';
import * as THREE from 'three';
import polylabel from 'polylabel';
import { jsPDF } from 'jspdf';
import {
  UNITS_API_BASE, SITEMAP_API_V2_SCENE, SITEMAP_API_V2_VIEWPORT, SITEMAP_API_V2_IMPORT,
  SITEMAP_API_V2_IMPORT_STATUS, ADMIN_API_BASE, ORG_API,
  CUSTOMER_API_BASE, UPLOAD_API, UNIT_RENTAL_API_BASE,
  UNITS_API_V2_SITE_PLAN, UNITS_API_V2_BULK_SITE_PLAN,
} from '../env';
import { computeUnitPricing, round } from '../utils/pricing';
import LegendSiteMap from './LegendSiteMap.vue';
import {
  clearFacilityCache,
  SITEMAP_CACHE_HEADER,
  SITEMAP_CACHE_VALUE,
} from '../sw-sitemap-register';

// Dedicated axios instance whose GET requests are tagged with the
// service-worker opt-in header. Only requests sent through this
// instance are eligible to be cached by sw-sitemap.js.
const facilitySceneAxios = axios.create();
facilitySceneAxios.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get') return config;

  const headers = Object.assign({}, config.headers, {
    [SITEMAP_CACHE_HEADER]: SITEMAP_CACHE_VALUE,
  });
  return Object.assign({}, config, { headers });
});

export default {
  name: 'FacilityScene',
  components: { LegendSiteMap },
  props: { facility: { type: String } },
  data() {
    return {
      loading: false,
      importing: false,
      loadingCustomers: false,
      importStatus: null,
      importError: null,
      importPollTimer: null,
      uiMode: 'view',
      activeTool: 'select',
      isSceneHelpCollapsed: true,
      enableLeaseStatus: false,
      enableParking: false,
      facilityData: null,
      org: null,
      units: [],
      customers: {},
      leases: [],
      unitRentals: [],
      selectedUnit: null,
      selectedUnitIds: [],
      activeMultiUnitPanel: '',
      scene: null,
      sceneData: null,
      // Three.js internals
      threeScene: null,
      threeCamera: null,
      threeRenderer: null,
      unitMeshes: [],
      overlayMeshes: [],
      dxfBgMesh: null,
      dxfBgRenderTarget: null,
      // Pan/zoom state
      isPanning: false,
      panStart: { x: 0, y: 0 },
      lastTouchDist: 0,
      abortController: null,
      // Drawing state
      drawStart: null,
      draftRectPoints: [],
      polygonPoints: [],
      polygonPreviewPoint: null,
      selectionRectStart: null,
      selectionRectEnd: null,
      selectGesture: null,
      touchSelectGesture: null,
      unitClickPosition: null,
      // Drag-move state
      isDraggingUnits: false,
      hasDraggedUnits: false,
      dragStartClient: null,
      dragStartWorld: null,
      dragUnitSnapshot: null,
      // Vertex drag state (resize/reshape)
      isDraggingVertex: false,
      dragVertexUnitId: null,
      dragVertexIndex: null,
      dragVertexSnapshot: null,
      // Pending assignment
      pendingAssignment: null,
      assignDialogVisible: false,
      assignForm: { unitId: '' },
      renameDialogVisible: false,
      renameForm: { unitId: '', sourceUnitId: '' },
      savingSitePlan: false,
      viewportSaving: false,
      pdfLoading: false,
      isFullscreen: false,
      isViewCentered: true,
      deleteConfirmVisible: false,
      deleteConfirmMessage: '',
      legendPopoverVisible: false,
      canvasToasts: [],
      canvasToastId: 0,
      // Snapshot of units before entering edit mode (for cancel)
      editSnapshot: null,
      // Track units modified locally during edit mode (persisted on Save)
      dirtyUnitIds: [],
      // Scale factor applied to the DXF background plane so it aligns with
      // unit overlay coordinates (the DXF parser already divides by 5).
      dxfDisplayScale: 0.2,
      controlHandlers: null,
      swMessageHandler: null,
    };
  },
  computed: {
    hasAccess() {
      const roles = this.$keycloak.tokenParsed &&
        this.$keycloak.tokenParsed.realm_access &&
        this.$keycloak.tokenParsed.realm_access.roles
        ? this.$keycloak.tokenParsed.realm_access.roles
        : [];
      return roles.includes('level_1') || roles.includes('level_2') || roles.includes('level_3');
    },
    isEditMode() {
      return this.hasAccess && this.uiMode === 'edit';
    },
    uploadApi() {
      return `${UPLOAD_API}/location/${this.facility}`;
    },
    uploadHeaders() {
      return { Authorization: `Bearer ${this.$keycloak.token}` };
    },
    importButtonText() {
      if (this.importing) return 'Uploading...';
      if (this.importStatus === 'queued' || this.importStatus === 'processing') return 'Importing...';
      return 'Import DXF';
    },
    securityFeeEnabled() {
      return this.facilityData && this.facilityData.securityFee && this.facilityData.securityFee.enabled && this.facilityData.securityFee.fee > 0;
    },
    selectedUnitPrice() {
      if (this.selectedUnit && this.facilityData) {
        return computeUnitPricing(this.selectedUnit, this.facilityData);
      }
      return 0;
    },
    canCopy() {
      return this.isEditMode && this.selectedUnitIds.length === 1;
    },
    canRename() {
      return this.isEditMode && this.selectedUnitIds.length === 1;
    },
    canDelete() {
      return this.isEditMode && this.selectedUnitIds.length > 0;
    },
    hasImportAccess() {
      const roles = this.$keycloak.tokenParsed &&
        this.$keycloak.tokenParsed.realm_access &&
        this.$keycloak.tokenParsed.realm_access.roles
        ? this.$keycloak.tokenParsed.realm_access.roles
        : [];
      return roles.indexOf('level_1') > -1;
    },
    isIOS() {
      if (typeof navigator === 'undefined') return false;
      const ua = navigator.userAgent || '';
      const platform = navigator.platform || '';
      const isIPad = /iPad/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isIPhone = /iPhone/.test(ua) || /iPod/.test(ua);
      return isIPad || isIPhone;
    },
    isIPhone() {
      if (typeof navigator === 'undefined') return false;
      const ua = navigator.userAgent || '';
      return /iPhone/.test(ua) || /iPod/.test(ua);
    },
    supportsFullscreen() {
      if (this.isIPhone) return false;
      if (typeof document === 'undefined') return false;
      return !!document.fullscreenEnabled;
    },
    assignableUnits() {
      // A unit is assignable if it has no visible polygon on the map.
      // It has a visible polygon when sitePlan.points exists
      // AND it isn't hidden. Legacy units with x/y/width/length also count
      // as already drawn (rendered via getNormalizedSitePlan fallback).
      const available = [];
      for (let i = 0; i < this.units.length; i++) {
        const unit = this.units[i];
        const sitePlan = unit.sitePlan || {};

        const isHidden = sitePlan.hidden === true;
        const hasSitePlanPoints = sitePlan.points && sitePlan.points.length >= 6;
        const hasVisibleGeometry = !isHidden && hasSitePlanPoints;

        if (!hasVisibleGeometry) {
          available.push(unit);
        }
      }

      available.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      return available;
    },
    renameAssignableUnits() {
      // Same as assignableUnits but excludes the unit we're renaming from.
      const sourceId = this.renameForm.sourceUnitId;
      return this.assignableUnits.filter(unit => unit._id !== sourceId);
    },
    unitPanelStyle() {
      if (!this.unitClickPosition || !this.$refs.sceneContainer) return {};
      const container = this.$refs.sceneContainer;
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const panelW = 300;
      const panelH = 350;
      let x = this.unitClickPosition.x + 12;
      let y = this.unitClickPosition.y - 20;
      if (x + panelW > cw) x = this.unitClickPosition.x - panelW - 12;
      if (y + panelH > ch) y = ch - panelH - 12;
      if (y < 12) y = 12;
      if (x < 12) x = 12;
      return { left: `${x}px`, top: `${y}px`, right: 'auto' };
    },
    selectedUnitsDetailed() {
      const self = this;
      return this.selectedUnitIds
        .map(id => self.units.find(u => u._id === id))
        .filter(Boolean)
        .map(unit => self.hydrateUnitDetails(unit));
    },
  },
  watch: {
    async facility() {
      if (this.loading) {
        this.abortController.abort();
        this.abortController = new AbortController();
      }
      await this.initScene();
    },
    selectedUnitIds(ids) {
      if (ids.length > 1) {
        this.activeMultiUnitPanel = ids[0] || '';
      }
    },
  },
  async mounted() {
    this.abortController = new AbortController();
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    this.$root.$on('layout-resize', this.handleResize);
    if ('serviceWorker' in navigator) {
      this.swMessageHandler = (event) => {
        if (event.data && event.data.type === 'CACHE_UPDATED') {
          this.handleSwCacheUpdate(event.data);
        }
      };
      navigator.serviceWorker.addEventListener('message', this.swMessageHandler);
    }
    await this.initScene();
  },
  beforeDestroy() {
    if (this.importPollTimer) clearInterval(this.importPollTimer);
    this.teardownControls();
    if (this.dxfBgRenderTarget) {
      this.dxfBgRenderTarget.dispose();
      this.dxfBgRenderTarget = null;
    }
    if (this.threeRenderer) {
      this.threeRenderer.dispose();
    }
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    this.$root.$off('layout-resize', this.handleResize);
    if (this.swMessageHandler && 'serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', this.swMessageHandler);
      this.swMessageHandler = null;
    }
  },
  methods: {
    computeSecurityFee(size) {
      if (this.securityFeeEnabled) {
        return round((this.facilityData.securityFee.fee / 100) * size);
      }
      return 0;
    },
    async initScene() {
      this.loading = true;
      this.uiMode = 'view';
      this.activeTool = 'select';
      this.selectedUnit = null;
      this.selectedUnitIds = [];
      this.resetDragState();
      this.cancelInteraction();

      try {
        await this.getFacility();

        if (this.facilityData && !this.facilityData.enableSceneSitePlan) {
          this.$router.replace({ name: 'Facility Sitemap Legacy', params: { facility: this.facility } });
          return;
        }

        await Promise.all([
          this.getOrg(),
          this.getUnits().then(this.getFacilityLeases),
          this.loadSceneData(),
          this.checkImportStatus(),
        ]);

        this.$nextTick(() => {
          this.setupThreeScene();
          if (this.sceneData && this.sceneData.scene) {
            this.rasterizeDxfBackground(this.sceneData.scene);
          }
          this.renderScene();
        });
      } catch (err) {
        console.error('Failed to init scene:', err);
      } finally {
        this.loading = false;
      }

      this.getFacilityCustomers();
    },

    // ── Data Loading ──────────────────────────────────────────────────

    async getFacility() {
      const response = await facilitySceneAxios.get(`${ADMIN_API_BASE}/facilities/${this.facility}`, {
        signal: this.abortController.signal,
        headers: { Authorization: `Bearer ${this.$keycloak.token}` },
      });
      this.facilityData = response.data;
    },
    async getOrg() {
      try {
        const org = await facilitySceneAxios.get(`${ORG_API}/theme`, {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });
        this.org = { colors: org.data };
      } catch (err) {
        this.org = { colors: [] };
      }
    },
    async getUnits() {
      const units = await facilitySceneAxios.get(`${UNITS_API_BASE}/${this.facility}`, {
        signal: this.abortController.signal,
        headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        params: {
          limit: 250,
          populate: 'invoice',
          types: this.enableParking ? '' : 'Warehouse,Office,Other',
        },
      });
      this.units = units.data;
      if (this.threeScene) {
        this.$nextTick(() => {
          this.renderUnitOverlays();
          this.threeRenderer.render(this.threeScene, this.threeCamera);
        });
      }
    },
    async loadSceneData({ noCache } = {}) {
      try {
        const headers = { Authorization: `Bearer ${this.$keycloak.token}` };
        if (noCache) {
          headers['Cache-Control'] = 'no-cache';
        }
        const response = await facilitySceneAxios.get(SITEMAP_API_V2_SCENE(this.facility), {
          signal: this.abortController.signal,
          headers,
        });
        this.sceneData = response.data;
        // The viewport now lives on the sitemap document, not the facility.
        // Merge it into facilityData so applyDefaultViewport and the
        // Clear button's disabled-state continue to work without change.
        if (response.data && 'sitePlanViewport' in response.data) {
          this.facilityData = Object.assign({}, this.facilityData, {
            sitePlanViewport: response.data.sitePlanViewport,
          });
          this.$root.$data.setFacilityDataAction(this.facilityData);
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          this.sceneData = null;
        } else {
          throw err;
        }
      }
    },
    getFacilityLeases() {
      this.leases = this.units.filter(unit => unit.invoice).map(unit => unit.invoice);
      this.unitRentals = [];
      for (const lease of this.leases) {
        this.unitRentals.push(...(lease.unitRentals || []));
      }
    },
    async getFacilityCustomers() {
      try {
        this.loadingCustomers = true;
        const response = await facilitySceneAxios.get(`${CUSTOMER_API_BASE}/customers/v3/facility/${this.facility}`, {
          signal: this.abortController.signal,
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });
        this.applyCustomers(response.data);
      } catch (err) {
        console.error('Failed to load customers:', err);
      } finally {
        this.loadingCustomers = false;
      }
    },

    /**
     * Filters customers to those attached to the current unit set, computes
     * each customer's `ach` sum from its `achTransactions`, and `$set`s the
     * result into `this.customers`.
     *
     * @param {Array} customers - Raw customer list from the API or SW cache.
     * @param {boolean} [overwrite=false] - When true, existing entries are
     *   refreshed (used by SW cache updates so stale data is overwritten).
     */
    applyCustomers(customers, overwrite = false) {
      const unitsCustomers = new Set(this.units.map(unit => unit.customer));
      const filtered = (customers || []).filter(customer => customer && unitsCustomers.has(customer._id));
      for (const customer of filtered) {
        if (!overwrite && this.customers[customer._id]) continue;
        const ach = (customer.achTransactions && customer.achTransactions.length)
          ? customer.achTransactions.map(t => t.amount).reduce((pv, nx) => pv + nx, 0)
          : 0;
        this.$set(this.customers, customer._id, Object.assign({}, customer, { ach }));
      }
    },

    unitsUpdateMatchesCurrentFilter(url) {
      // The units endpoint is filtered by a `types` query param driven by
      // `enableParking`. A background SW revalidation may have fetched the
      // opposite variant; ignore those so the current view's dataset is not
      // silently swapped.
      let types = null;
      try {
        types = new URL(url).searchParams.get('types');
      } catch (e) {
        return false;
      }
      const expected = this.enableParking ? '' : 'Warehouse,Office,Other';
      return types === expected;
    },

    /**
     * Applies a service-worker cache update for the facility, scene, units,
     * or customers endpoint, refreshing the relevant view state. A scene
     * update whose `sitePlanViewport` changed is applied to the camera in
     * place; all other updates preserve pan/zoom. Skipped while in edit mode.
     *
     * @param {Object} eventData - SW message payload.
     * @param {string} eventData.url - Revalidated request URL.
     * @param {*} eventData.data - Fresh response data.
     */
    handleSwCacheUpdate(eventData) {
      if (!eventData || !this.facility || this.isEditMode) return;
      const { url, data } = eventData;
      if (!data) return;

      const sceneUrl = SITEMAP_API_V2_SCENE(this.facility);
      const facilityUrl = `${ADMIN_API_BASE}/facilities/${this.facility}`;
      const unitsUrl = `${UNITS_API_BASE}/${this.facility}`;
      const customersUrl = `${CUSTOMER_API_BASE}/customers/v2/facility/${this.facility}`;

      let updated = false;
      if (url.indexOf(facilityUrl) === 0) {
        // Refresh facility data (pricing, security fee, enableSceneSitePlan,
        // etc.). Root state is kept in sync so other components (LegendSiteMap,
        // header, etc.) see the updated facility. The viewport now lives on
        // the sitemap document, so it is preserved from the current value.
        const preservedViewport = this.facilityData && this.facilityData.sitePlanViewport;
        this.facilityData = Object.assign({}, data, { sitePlanViewport: preservedViewport });
        this.$root.$data.setFacilityDataAction(this.facilityData);
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            if (!this.threeScene || !this.threeRenderer || !this.threeCamera) return;
            this.renderUnitOverlays();
            this.threeRenderer.render(this.threeScene, this.threeCamera);
          });
        });
        updated = true;
      } else if (url.indexOf(sceneUrl) === 0) {
        // Rebuild DXF background. The viewport now lives on the sitemap
        // document, so detect viewport changes here and apply in place.
        const previousViewport = this.facilityData && this.facilityData.sitePlanViewport;
        const nextViewport = data && data.sitePlanViewport;
        const viewportChanged = JSON.stringify(previousViewport || null)
          !== JSON.stringify(nextViewport || null);

        this.sceneData = data;
        if ('sitePlanViewport' in data) {
          this.facilityData = Object.assign({}, this.facilityData, { sitePlanViewport: data.sitePlanViewport });
          this.$root.$data.setFacilityDataAction(this.facilityData);
        }
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            if (!this.threeScene || !this.threeRenderer) return;
            if (this.sceneData && this.sceneData.scene) {
              this.rasterizeDxfBackground(this.sceneData.scene);
            }
            if (viewportChanged && this.threeCamera && this.isViewCentered) {
              // Only snap to a changed default viewport when the user hasn't
              // panned/zoomed away; otherwise preserve their current view.
              const { width, height } = this.getSceneViewportSize();
              this.applyDefaultViewport(width, height);
            }
            this.renderScene();
          });
        });
        updated = true;
      } else if (url.indexOf(unitsUrl) === 0) {
        // Ignore updates whose `types` filter doesn't match the current view.
        if (!this.unitsUpdateMatchesCurrentFilter(url)) return;

        // Refresh overlays/leases, preserving pan/zoom.
        this.units = data;
        this.getFacilityLeases();
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            if (!this.threeScene || !this.threeRenderer || !this.threeCamera) return;
            this.renderUnitOverlays();
            this.syncSelectedUnit();
            this.threeRenderer.render(this.threeScene, this.threeCamera);
          });
        });
        updated = true;
      } else if (url.indexOf(customersUrl) === 0) {
        this.applyCustomers(data, true);
        updated = true;
      }

      if (updated) {
        this.showCanvasToast('Loaded latest site plan', 'info');
      }
    },

    beforeDxfUpload(file) {
      if (!this.hasImportAccess) {
        this.showCanvasToast('Only administrators can import DXF files', 'error');
        return false;
      }
      const isDxf = file.name.toLowerCase().endsWith('.dxf');
      if (!isDxf) {
        this.showCanvasToast('Only DXF files are supported', 'error');
        return false;
      }
      this.importing = true;
      return true;
    },
    async handleDxfUpload(response) {
      this.importing = false;
      // Clear cached data for this facility so stale sitemap/units
      // are not served after the DXF import updates the backend.
      clearFacilityCache(this.facility);
      try {
        await axios.post(SITEMAP_API_V2_IMPORT(this.facility), {
          key: response.url,
          fileName: response.url.split('/').pop(),
          size: 0,
        }, {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });
        this.importStatus = 'queued';
        this.showCanvasToast('DXF uploaded. Import started.', 'success');
        this.startImportPolling();
      } catch (err) {
        this.showCanvasToast((err.response && err.response.data) || 'Failed to start import', 'error');
      }
    },
    async checkImportStatus() {
      try {
        const response = await facilitySceneAxios.get(SITEMAP_API_V2_IMPORT_STATUS(this.facility), {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });
        const importJob = response.data && response.data.importJob;
        this.importStatus = importJob && importJob.status;
        this.importError = importJob && importJob.error;
        if (this.importStatus === 'queued' || this.importStatus === 'processing') {
          this.startImportPolling();
        }
      } catch (err) {
        // ignore — no import exists yet
      }
    },
    startImportPolling() {
      if (this.importPollTimer) clearInterval(this.importPollTimer);
      this.importPollTimer = setInterval(async () => {
        try {
          const response = await facilitySceneAxios.get(SITEMAP_API_V2_IMPORT_STATUS(this.facility), {
            headers: { Authorization: `Bearer ${this.$keycloak.token}` },
          });
          const importJob = response.data && response.data.importJob;
          this.importStatus = importJob && importJob.status;
          this.importError = importJob && importJob.error;
          if (this.importStatus === 'completed') {
            clearInterval(this.importPollTimer);
            this.importPollTimer = null;
            this.showCanvasToast('DXF import completed!', 'success');
            await this.loadSceneData({ noCache: true });
            this.setupThreeScene();
            if (this.sceneData && this.sceneData.scene) {
              this.rasterizeDxfBackground(this.sceneData.scene);
            }
            this.renderScene();
          } else if (this.importStatus === 'failed') {
            clearInterval(this.importPollTimer);
            this.importPollTimer = null;
            this.showCanvasToast(`Import failed: ${this.importError || 'Unknown error'}`, 'error');
          }
        } catch (err) {
          // keep polling
        }
      }, 3000);
    },

    // ── Three.js Setup ───────────────────────────────────────────────

    getSceneBounds() {
      let unitMaxX = 0;
      let unitMaxY = 0;
      for (let ui = 0; ui < this.units.length; ui++) {
        const u = this.units[ui];
        const sp = u.sitePlan;
        if (sp && sp.points && sp.points.length >= 6) {
          for (let pi = 0; pi < sp.points.length; pi += 2) {
            if (sp.points[pi] > unitMaxX) unitMaxX = sp.points[pi];
            if (sp.points[pi + 1] > unitMaxY) unitMaxY = sp.points[pi + 1];
          }
        } else {
          const ux = (u.x || 0) + (u.width || 50);
          const uy = (u.y || 0) + (u.length || 50);
          if (ux > unitMaxX) unitMaxX = ux;
          if (uy > unitMaxY) unitMaxY = uy;
        }
      }

      let bboxW,
        bboxH;
      if (this.sceneData && this.sceneData.scene) {
        const meta = this.sceneData.sceneMeta;
        const dxfW = (meta && meta.bbox && meta.bbox[2]) ? meta.bbox[2] * this.dxfDisplayScale : 0;
        const dxfH = (meta && meta.bbox && meta.bbox[3]) ? meta.bbox[3] * this.dxfDisplayScale : 0;
        bboxW = Math.max(unitMaxX, dxfW, 200);
        bboxH = Math.max(unitMaxY, dxfH, 150);
      } else {
        bboxW = Math.max(unitMaxX, 200);
        bboxH = Math.max(unitMaxY, 150);
      }
      return { bboxW, bboxH };
    },

    // # Camera state model
    //
    // The orthographic frustum (left/right/top/bottom) represents the
    // "zoom=1" base view, sized to fit the scene bounds with 10% padding
    // at the current viewport aspect ratio. User zoom lives entirely in
    // `camera.zoom`; user pan lives entirely in `camera.position`.
    //
    //   updateCameraToFit       — reset to fit-to-bounds (zoom=1, centered).
    //                             Used for: PDF export, resize fallback,
    //                             and "Fit Entire Site" action.
    //   applyDefaultViewport    — set the saved default viewport (if any),
    //                             otherwise fall back to fit-to-bounds.
    //                             Used for: initial setup, Center View.
    //   updateCameraForResize   — adjust the base frustum to a new viewport
    //                             size while preserving user zoom + pan.
    //                             Used for: window resize, fullscreen toggle.
    //   applyZoom(factor)       — multiply user zoom (wheel + pinch).
    //   applyPan(dxPx, dyPx)    — translate user pan in screen pixels
    //                             (mouse drag + 2-finger drag).

    applyDefaultViewport(width, height) {
      if (!this.threeCamera) return;
      // Establish the base frustum + centered fit first.
      this.updateCameraToFit(width, height);

      const viewport = this.facilityData && this.facilityData.sitePlanViewport;
      if (!viewport) return;

      // Recompute zoom from the stored visible world height so the saved
      // view is stable across different canvas sizes / aspect ratios.
      const baseViewHeight = this.threeCamera.top - this.threeCamera.bottom;
      let zoom = 1;
      if (viewport.viewHeight && viewport.viewHeight > 0) {
        zoom = baseViewHeight / viewport.viewHeight;
      } else if (viewport.zoom) {
        zoom = viewport.zoom;
      }
      this.threeCamera.zoom = Math.max(0.05, Math.min(100, zoom));
      const centerX = Number(viewport.centerX);
      const centerY = Number(viewport.centerY);
      this.threeCamera.position.set(
        Number.isFinite(centerX) ? centerX : this.threeCamera.position.x,
        Number.isFinite(centerY) ? centerY : this.threeCamera.position.y,
        100,
      );
      this.threeCamera.lookAt(this.threeCamera.position.x, this.threeCamera.position.y, 0);
      this.threeCamera.updateProjectionMatrix();
      this.isViewCentered = true;
    },

    updateCameraToFit(width, height) {
      if (!this.threeCamera) return;

      const bounds = this.getSceneBounds();
      const aspect = width / height;
      const sceneAspect = bounds.bboxW / bounds.bboxH;
      let viewW;
      let viewH;

      if (aspect > sceneAspect) {
        viewH = bounds.bboxH * 1.1;
        viewW = viewH * aspect;
      } else {
        viewW = bounds.bboxW * 1.1;
        viewH = viewW / aspect;
      }

      this.threeCamera.left = -viewW / 2;
      this.threeCamera.right = viewW / 2;
      this.threeCamera.top = viewH / 2;
      this.threeCamera.bottom = -viewH / 2;
      this.threeCamera.zoom = 1;
      this.threeCamera.position.set(bounds.bboxW / 2, bounds.bboxH / 2, 100);
      this.threeCamera.lookAt(bounds.bboxW / 2, bounds.bboxH / 2, 0);
      this.threeCamera.updateProjectionMatrix();
      this.isViewCentered = true;
    },

    updateCameraForResize(width, height) {
      if (!this.threeCamera) return;

      const aspect = width / height;
      const centerX = this.threeCamera.position.x;
      const centerY = this.threeCamera.position.y;
      const viewH = this.threeCamera.top - this.threeCamera.bottom;

      if (!isFinite(viewH) || viewH <= 0) {
        this.updateCameraToFit(width, height);
        return;
      }

      // Keep the vertical world span at zoom=1 constant; only adjust the
      // horizontal extents for the new aspect. Preserves user zoom + pan.
      const viewW = viewH * aspect;
      this.threeCamera.left = -viewW / 2;
      this.threeCamera.right = viewW / 2;
      this.threeCamera.top = viewH / 2;
      this.threeCamera.bottom = -viewH / 2;
      this.threeCamera.position.set(centerX, centerY, this.threeCamera.position.z || 100);
      this.threeCamera.lookAt(centerX, centerY, 0);
      this.threeCamera.updateProjectionMatrix();
    },

    applyZoom(factor) {
      if (!this.threeCamera || !isFinite(factor) || factor <= 0) return;
      const next = (this.threeCamera.zoom || 1) * factor;
      // Gentle clamp to keep the view usable.
      this.threeCamera.zoom = Math.max(0.05, Math.min(100, next));
      this.threeCamera.updateProjectionMatrix();
      this.isViewCentered = false;
    },

    applyPan(dxPx, dyPx) {
      if (!this.threeCamera) return;
      const container = this.$refs.sceneContainer;
      const width = (container && container.clientWidth) || 1;
      const height = (container && container.clientHeight) || 600;
      const zoom = this.threeCamera.zoom || 1;
      // Visible world units per screen pixel = (frustum size / zoom) / canvas size.
      const scaleX = (this.threeCamera.right - this.threeCamera.left) / zoom / width;
      const scaleY = (this.threeCamera.top - this.threeCamera.bottom) / zoom / height;
      this.threeCamera.position.x -= dxPx * scaleX;
      this.threeCamera.position.y += dyPx * scaleY;
      this.threeCamera.updateProjectionMatrix();
      if (dxPx !== 0 || dyPx !== 0) this.isViewCentered = false;
    },

    getSceneViewportSize() {
      const container = this.$refs.sceneContainer;
      const rect = container && container.getBoundingClientRect();
      const width = Math.max(1, Math.round((rect && rect.width) || 1));
      const height = Math.max(1, Math.round((rect && rect.height) || 1));
      return { width, height };
    },

    centerView() {
      if (!this.threeCamera || !this.threeRenderer) return;
      const { width, height } = this.getSceneViewportSize();
      this.applyDefaultViewport(width, height);
      this.renderScene();
    },

    fitEntireSite() {
      if (!this.threeCamera || !this.threeRenderer) return;
      const { width, height } = this.getSceneViewportSize();
      this.updateCameraToFit(width, height);
      this.renderScene();
    },

    getCurrentViewport() {
      if (!this.threeCamera) return null;
      const zoom = this.threeCamera.zoom || 1;
      return {
        centerX: this.threeCamera.position.x,
        centerY: this.threeCamera.position.y,
        zoom,
        viewHeight: (this.threeCamera.top - this.threeCamera.bottom) / zoom,
      };
    },

    async saveDefaultViewport() {
      if (this.viewportSaving) return;
      const facility = this.facility;
      const sitePlanViewport = this.getCurrentViewport();
      if (!sitePlanViewport) return;
      this.viewportSaving = true;
      try {
        const response = await axios.put(SITEMAP_API_V2_VIEWPORT(facility), { sitePlanViewport }, {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });
        if (facility !== this.facility) return;
        const viewport = response.data && response.data.sitePlanViewport;
        this.facilityData = Object.assign({}, this.facilityData, { sitePlanViewport: viewport });
        this.$root.$data.setFacilityDataAction(this.facilityData);
        // The viewport lives on the cached scene response, so invalidate
        // the stale SW entry for this facility so a later reload does not
        // re-apply the old viewport.
        clearFacilityCache(this.facility);
        this.isViewCentered = true;
        this.showCanvasToast('Default viewport saved', 'success');
      } catch (err) {
        if (facility !== this.facility) return;
        this.showCanvasToast(`Failed to save viewport: ${(err.response && err.response.data) || err.message}`, 'error');
      } finally {
        this.viewportSaving = false;
      }
    },

    async clearDefaultViewport() {
      if (this.viewportSaving) return;
      const facility = this.facility;
      this.viewportSaving = true;
      try {
        await axios.put(SITEMAP_API_V2_VIEWPORT(facility), { sitePlanViewport: null }, {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });
        if (facility !== this.facility) return;
        this.facilityData = Object.assign({}, this.facilityData, { sitePlanViewport: null });
        this.$root.$data.setFacilityDataAction(this.facilityData);
        clearFacilityCache(this.facility);
        this.fitEntireSite();
        this.showCanvasToast('Default viewport cleared', 'success');
      } catch (err) {
        if (facility !== this.facility) return;
        this.showCanvasToast(`Failed to clear viewport: ${(err.response && err.response.data) || err.message}`, 'error');
      } finally {
        this.viewportSaving = false;
      }
    },

    setupThreeScene() {
      const container = this.$refs.sceneContainer;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const background = 'rgb(255,255,204)';

      if (this.threeRenderer) {
        this.threeRenderer.dispose();
      }

      this.threeScene = new THREE.Scene();
      this.threeCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);

      this.threeRenderer = new THREE.WebGLRenderer({
        canvas: this.$refs.canvas,
        antialias: true,
      });
      this.threeRenderer.setSize(width, height);
      this.threeRenderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.threeRenderer.setClearColor(background);

      // Apply the saved default viewport (if any) on top of the base fit.
      this.applyDefaultViewport(width, height);

      // Setup controls
      this.setupControls();
      window.removeEventListener('resize', this.handleResize);
      window.addEventListener('resize', this.handleResize);
    },

    renderScene() {
      if (!this.threeScene || !this.threeRenderer) return;

      // Clear existing objects
      while (this.threeScene.children.length > 0) {
        const obj = this.threeScene.children[0];
        this.threeScene.remove(obj);
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
      }
      this.unitMeshes = [];

      // Add cached DXF background texture as a single quad
      if (this.dxfBgMesh) {
        this.threeScene.add(this.dxfBgMesh);
      }

      // Render DXF text labels (between background z=0 and unit overlays z=0.5)
      this.renderDxfTexts();

      // Render unit overlays
      this.renderUnitOverlays();
      this.renderInteractionOverlay();

      this.threeRenderer.render(this.threeScene, this.threeCamera);
    },

    rasterizeDxfBackground(scene) {
      if (!this.threeRenderer) return;

      // Dispose previous render target
      if (this.dxfBgRenderTarget) {
        this.dxfBgRenderTarget.dispose();
        this.dxfBgRenderTarget = null;
      }
      if (this.dxfBgMesh) {
        if (this.dxfBgMesh.geometry) this.dxfBgMesh.geometry.dispose();
        if (this.dxfBgMesh.material) this.dxfBgMesh.material.dispose();
        this.dxfBgMesh = null;
      }

      const meta = this.sceneData && this.sceneData.sceneMeta;
      const bboxW = (meta && meta.bbox && meta.bbox[2]) || 200;
      const bboxH = (meta && meta.bbox && meta.bbox[3]) || 150;

      // Build offscreen scene with DXF entities
      const offscreenScene = new THREE.Scene();
      offscreenScene.background = null; // transparent


      const ignoredLayers = { 'CS-Unit Size': true, 'CS-Dimensions': true, 'A-FLOR-IDEN': true, 'RS-Unit Numbers': true, 'RS-Unit Sizes': true };
      const renderables = scene.renderables || {};

      // Lines
      const lines = renderables.lines || [];
      for (let li = 0; li < lines.length; li++) {
        const line = lines[li];
        if (ignoredLayers[line.layer]) continue;
        const points = [];
        for (let i = 0; i < line.points.length; i += 2) {
          points.push(new THREE.Vector3(line.points[i], line.points[i + 1], 0));
        }
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({ color: '#000000' });
        offscreenScene.add(new THREE.Line(geometry, material));
      }

      // Fills
      const fills = renderables.fills || [];
      for (let fi = 0; fi < fills.length; fi++) {
        const fill = fills[fi];
        if (ignoredLayers[fill.layer]) continue;
        const shape = new THREE.Shape();
        shape.moveTo(fill.points[0], fill.points[1]);
        for (let j = 2; j < fill.points.length; j += 2) {
          shape.lineTo(fill.points[j], fill.points[j + 1]);
        }
        shape.closePath();
        const fillGeom = new THREE.ShapeGeometry(shape);
        const fillMat = new THREE.MeshBasicMaterial({
          color: '#000000',
          transparent: true,
          opacity: 0.3,
          side: THREE.DoubleSide,
        });
        offscreenScene.add(new THREE.Mesh(fillGeom, fillMat));

        // Outline
        const edgePoints = [];
        for (let k = 0; k < fill.points.length; k += 2) {
          edgePoints.push(new THREE.Vector3(fill.points[k], fill.points[k + 1], 0.05));
        }
        edgePoints.push(edgePoints[0].clone());
        const edgeGeom = new THREE.BufferGeometry().setFromPoints(edgePoints);
        const edgeMat = new THREE.LineBasicMaterial({ color: '#000000' });
        offscreenScene.add(new THREE.Line(edgeGeom, edgeMat));
      }

      // Arcs
      const arcs = renderables.arcs || [];
      for (let ai = 0; ai < arcs.length; ai++) {
        const arc = arcs[ai];
        if (ignoredLayers[arc.layer]) continue;
        const arcPoints = [];
        for (let m = 0; m < arc.points.length; m += 2) {
          arcPoints.push(new THREE.Vector3(arc.points[m], arc.points[m + 1], 0));
        }
        const arcGeom = new THREE.BufferGeometry().setFromPoints(arcPoints);
        const arcMat = new THREE.LineBasicMaterial({ color: '#000000' });
        offscreenScene.add(new THREE.Line(arcGeom, arcMat));
      }

      // Render to offscreen target
      const renderTarget = new THREE.WebGLRenderTarget(4096, 4096, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
      });
      this.dxfBgRenderTarget = renderTarget;

      const offscreenCamera = new THREE.OrthographicCamera(-bboxW / 2, bboxW / 2, bboxH / 2, -bboxH / 2, 0.1, 1000);
      offscreenCamera.position.set(bboxW / 2, bboxH / 2, 100);
      offscreenCamera.lookAt(bboxW / 2, bboxH / 2, 0);

      const prevTarget = this.threeRenderer.getRenderTarget();
      this.threeRenderer.setRenderTarget(renderTarget);
      this.threeRenderer.setClearColor(0x000000, 0);
      this.threeRenderer.clear();
      this.threeRenderer.render(offscreenScene, offscreenCamera);
      this.threeRenderer.setRenderTarget(prevTarget);

      // Restore main clear color
      this.threeRenderer.setClearColor('rgb(255,255,204)');

      // Dispose offscreen scene
      while (offscreenScene.children.length > 0) {
        const child = offscreenScene.children[0];
        offscreenScene.remove(child);
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      }

      // Create a single textured plane scaled down to match unit geometry
      const dispW = bboxW * this.dxfDisplayScale;
      const dispH = bboxH * this.dxfDisplayScale;
      const planeGeom = new THREE.PlaneGeometry(dispW, dispH);
      const planeMat = new THREE.MeshBasicMaterial({
        map: renderTarget.texture,
        transparent: true,
        side: THREE.DoubleSide,
      });
      this.dxfBgMesh = new THREE.Mesh(planeGeom, planeMat);
      this.dxfBgMesh.position.set(dispW / 2, dispH / 2, 0);
    },

    renderDxfTexts() {
      if (!this.sceneData || !this.sceneData.scene) return;
      const texts = this.sceneData.scene.renderables && this.sceneData.scene.renderables.texts;
      if (!texts || !texts.length) return;

      const scale = this.dxfDisplayScale;
      const meta = this.sceneData.sceneMeta;
      const bboxW = (meta && meta.bbox && meta.bbox[2]) || 1000;
      const bboxH = (meta && meta.bbox && meta.bbox[3]) || 1000;
      const maxHeight = Math.sqrt(bboxW * bboxW + bboxH * bboxH) * scale * 0.02;
      for (let ti = 0; ti < texts.length; ti++) {
        const t = texts[ti];
        const color = t.layer === 'CS-Annotation' ? '#666666' : '#333333';
        const sizeMultiplier = t.layer === 'CS-Annotation' ? 1.5 : 1.5;
        const worldHeight = Math.min(t.height * scale * sizeMultiplier, maxHeight);
        const spriteInfo = this.createTextSprite(t.text, color, worldHeight);
        const sprite = spriteInfo.sprite || spriteInfo;
        const nLines = spriteInfo.lineCount || 1;
        const spriteW = sprite.scale.x;
        const spriteH = sprite.scale.y;

        // Offset sprite position based on MTEXT attachment point
        // Attachment points: 1=TL, 2=TC, 3=TR, 4=ML, 5=MC, 6=MR, 7=BL, 8=BC, 9=BR
        const ap = t.attachmentPoint || 5;
        let ox = 0;
        let oy = 0;
        if (ap === 1 || ap === 4 || ap === 7) ox = spriteW / 2; // left
        if (ap === 3 || ap === 6 || ap === 9) ox = -spriteW / 2; // right
        if (ap === 1 || ap === 2 || ap === 3) oy = -spriteH / 2; // top
        if (ap === 7 || ap === 8 || ap === 9) oy = spriteH / 2; // bottom

        sprite.position.set(t.x * scale + ox, t.y * scale + oy, 0.2);
        if (t.rotation) sprite.material.rotation = t.rotation * (Math.PI / 180);
        this.threeScene.add(sprite);
      }
    },

    renderUnitOverlays() {
      // Remove existing unit meshes
      for (const mesh of this.unitMeshes) {
        this.threeScene.remove(mesh);
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) mesh.material.dispose();
      }
      this.unitMeshes = [];

      for (const unit of this.units) {
        if (unit.sitePlan && unit.sitePlan.hidden) continue;

        const color = this.getUnitColor(this.enableLeaseStatus ? unit.leaseStatus : unit.status);
        const sitePlan = this.getNormalizedSitePlan(unit);
        if (!sitePlan) continue;

        const shape = this.getShapeFromPoints(sitePlan.points);
        const labelAnchor = sitePlan.labelAnchor;
        const isSelected = this.selectedUnitIds.indexOf(unit._id) > -1;

        // Fill mesh
        const geometry = new THREE.ShapeGeometry(shape);
        const material = new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: isSelected ? 0.85 : 0.55,
          side: THREE.DoubleSide,
        });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.position.z = 0.5;
        mesh.userData = { unitId: unit._id, type: 'unit' };
        this.threeScene.add(mesh);
        this.unitMeshes.push(mesh);

        // Border
        const borderPoints = [];
        const pts = shape.getPoints();
        for (const p of pts) {
          borderPoints.push(new THREE.Vector3(p.x, p.y, 0.55));
        }
        borderPoints.push(borderPoints[0].clone());
        const borderGeom = new THREE.BufferGeometry().setFromPoints(borderPoints);
        const borderMat = new THREE.LineBasicMaterial({ color: isSelected ? '#4cc9f0' : '#ffffff' });
        const borderLine = new THREE.Line(borderGeom, borderMat);
        borderLine.userData = { unitId: unit._id, type: 'unitBorder' };
        this.threeScene.add(borderLine);
        this.unitMeshes.push(borderLine);

        // Label
        const labelInfo = this.createTextSprite(unit.name, isSelected ? '#4cc9f0' : '#ffffff', 32);
        const label = labelInfo.sprite || labelInfo;
        label.position.set(labelAnchor[0], labelAnchor[1], 0.6);
        label.userData = { unitId: unit._id, type: 'unitLabel' };
        this.threeScene.add(label);
        this.unitMeshes.push(label);
      }
    },

    createTextSprite(text, color, size) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const fontSize = 64;
      const lineHeight = fontSize * 1.8;
      const cleaned = text || '';
      const lines = cleaned.split('\n');
      ctx.font = `bold ${fontSize}px Arial`;
      let maxWidth = 0;
      for (var i = 0; i < lines.length; i++) {
        const w = ctx.measureText(lines[i]).width;
        if (w > maxWidth) maxWidth = w;
      }
      canvas.width = maxWidth + 20;
      canvas.height = lineHeight * lines.length + 20;
      ctx.font = `bold ${fontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = color;
      const startY = (canvas.height - lineHeight * lines.length) / 2 + lineHeight / 2;
      for (var i = 0; i < lines.length; i++) {
        ctx.fillText(lines[i], canvas.width / 2, startY + i * lineHeight);
      }

      const texture = new THREE.CanvasTexture(canvas);
      texture.minFilter = THREE.LinearFilter;
      const material = new THREE.SpriteMaterial({ map: texture });
      const sprite = new THREE.Sprite(material);
      const aspect = canvas.width / canvas.height;
      const totalHeight = size * lines.length;
      sprite.scale.set(aspect * totalHeight, totalHeight, 1);
      return { sprite, lineCount: lines.length };
    },

    getUnitColor(status) {
      if (!status) return '#035AA6';
      if (!this.org || !this.org.colors) return '#035AA6';
      const theme = this.org.colors.find(item => item.type === status);
      return theme ? theme.color : '#035AA6';
    },

    updateUnitColors() {
      if (!this.threeScene) return;
      this.renderUnitOverlays();
      this.threeRenderer.render(this.threeScene, this.threeCamera);
    },

    // ── Geometry helpers ─────────────────────────────────────────────

    getNormalizedSitePlan(unit) {
      const sp = unit.sitePlan;
      if (sp && sp.points && sp.points.length >= 6) {
        const hasValidLabelAnchor = (
          sp.labelAnchor &&
          sp.labelAnchor.length === 2 &&
          this.isPointInPolygon(sp.labelAnchor[0], sp.labelAnchor[1], sp.points)
        );
        return {
          geometryType: sp.geometryType || 'polygon',
          points: sp.points.slice(),
          labelAnchor: hasValidLabelAnchor
            ? sp.labelAnchor.slice()
            : this.getLabelAnchorFromPoints(sp.points),
          rotation: typeof sp.rotation === 'number' ? sp.rotation : 0,
          hidden: !!sp.hidden,
        };
      }
      return null;
    },

    getShapeFromPoints(points) {
      const shape = new THREE.Shape();
      shape.moveTo(points[0], points[1]);
      for (let i = 2; i < points.length; i += 2) {
        shape.lineTo(points[i], points[i + 1]);
      }
      shape.closePath();
      return shape;
    },

    pointsToPolylabelRing(points) {
      const ring = [];
      for (let i = 0; i < points.length; i += 2) {
        ring.push([points[i], points[i + 1]]);
      }
      return [ring];
    },

    getLabelAnchorFromPoints(points) {
      if (!points || points.length < 6) return [0, 0];
      const polygon = this.pointsToPolylabelRing(points);
      const result = polylabel(polygon, 1.0);
      return [result[0], result[1]];
    },

    isPointInPolygon(x, y, points) {
      let inside = false;
      for (let i = 0, j = points.length - 2; i < points.length; j = i, i += 2) {
        const xi = points[i];
        const yi = points[i + 1];
        const xj = points[j];
        const yj = points[j + 1];
        const intersects = ((yi > y) !== (yj > y)) &&
          (x < ((xj - xi) * (y - yi) / (yj - yi)) + xi);
        if (intersects) inside = !inside;
      }
      return inside;
    },

    getBoundsFromPoints(points) {
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (let i = 0; i < points.length; i += 2) {
        if (points[i] < minX) minX = points[i];
        if (points[i] > maxX) maxX = points[i];
        if (points[i + 1] < minY) minY = points[i + 1];
        if (points[i + 1] > maxY) maxY = points[i + 1];
      }
      return { minX, minY, maxX, maxY };
    },

    clientToWorld(clientX, clientY) {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.threeCamera) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
      const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
      const vec = new THREE.Vector3(ndcX, ndcY, 0);
      vec.unproject(this.threeCamera);
      return { x: vec.x, y: vec.y };
    },

    buildRectPoints(start, end) {
      const x1 = Math.min(start.x, end.x);
      const y1 = Math.min(start.y, end.y);
      const x2 = Math.max(start.x, end.x);
      const y2 = Math.max(start.y, end.y);
      return [x1, y1, x2, y1, x2, y2, x1, y2];
    },

    boundsIntersect(a, b) {
      return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
    },

    // ── Overlay rendering ────────────────────────────────────────────

    clearOverlayMeshes() {
      for (let i = 0; i < this.overlayMeshes.length; i++) {
        const obj = this.overlayMeshes[i];
        this.threeScene.remove(obj);
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
      }
      this.overlayMeshes = [];
    },

    renderInteractionOverlay() {
      this.clearOverlayMeshes();
      if (!this.threeScene) return;

      // Draft rectangle
      if (this.draftRectPoints.length === 8) {
        this.renderDraftShape(this.draftRectPoints, '#4cc9f0', 0.3);
      }

      // Draft polygon
      if (this.polygonPoints.length >= 2) {
        const flatPts = [];
        for (let i = 0; i < this.polygonPoints.length; i++) {
          flatPts.push(this.polygonPoints[i].x, this.polygonPoints[i].y);
        }
        // Draw lines connecting polygon points
        const linePoints = [];
        for (let j = 0; j < this.polygonPoints.length; j++) {
          linePoints.push(new THREE.Vector3(this.polygonPoints[j].x, this.polygonPoints[j].y, 0.8));
        }
        if (this.polygonPreviewPoint) {
          linePoints.push(new THREE.Vector3(this.polygonPreviewPoint.x, this.polygonPreviewPoint.y, 0.8));
        }
        const lineGeom = new THREE.BufferGeometry().setFromPoints(linePoints);
        const lineMat = new THREE.LineBasicMaterial({ color: '#4cc9f0' });
        const line = new THREE.Line(lineGeom, lineMat);
        this.threeScene.add(line);
        this.overlayMeshes.push(line);

        // Draw dots at each vertex
        const baseRadius = this.getVertexDotRadius();
        for (let k = 0; k < this.polygonPoints.length; k++) {
          const isFirst = k === 0 && this.polygonPoints.length >= 3;
          const dotRadius = isFirst ? baseRadius * 1.8 : baseRadius;
          const dotGeom = new THREE.CircleGeometry(dotRadius, 16);
          const dotColor = isFirst ? '#00ff00' : '#4cc9f0';
          const dotMat = new THREE.MeshBasicMaterial({ color: dotColor });
          const dot = new THREE.Mesh(dotGeom, dotMat);
          dot.position.set(this.polygonPoints[k].x, this.polygonPoints[k].y, 0.85);
          this.threeScene.add(dot);
          this.overlayMeshes.push(dot);
        }
      } else if (this.polygonPoints.length === 1 && this.polygonPreviewPoint) {
        const singleLine = [];
        singleLine.push(new THREE.Vector3(this.polygonPoints[0].x, this.polygonPoints[0].y, 0.8));
        singleLine.push(new THREE.Vector3(this.polygonPreviewPoint.x, this.polygonPreviewPoint.y, 0.8));
        const slGeom = new THREE.BufferGeometry().setFromPoints(singleLine);
        const slMat = new THREE.LineBasicMaterial({ color: '#4cc9f0' });
        const sl = new THREE.Line(slGeom, slMat);
        this.threeScene.add(sl);
        this.overlayMeshes.push(sl);

        const d1Geom = new THREE.CircleGeometry(this.getVertexDotRadius(), 16);
        const d1Mat = new THREE.MeshBasicMaterial({ color: '#4cc9f0' });
        const d1 = new THREE.Mesh(d1Geom, d1Mat);
        d1.position.set(this.polygonPoints[0].x, this.polygonPoints[0].y, 0.85);
        this.threeScene.add(d1);
        this.overlayMeshes.push(d1);
      }

      // Vertex handles for selected units in edit mode
      if (this.isEditMode && this.selectedUnitIds.length >= 1 && this.activeTool === 'select') {
        const handleRadius = this.getVertexDotRadius() * 1.2;
        for (var vi = 0; vi < this.selectedUnitIds.length; vi++) {
          const vUnit = this.units.find(u => u._id === this.selectedUnitIds[vi]);
          if (!vUnit) continue;
          const vSp = this.getNormalizedSitePlan(vUnit);
          if (!vSp || vSp.points.length < 6) continue;
          for (let vp = 0; vp < vSp.points.length; vp += 2) {
            const hGeom = new THREE.CircleGeometry(handleRadius, 16);
            const hMat = new THREE.MeshBasicMaterial({ color: '#ffffff' });
            const hMesh = new THREE.Mesh(hGeom, hMat);
            hMesh.position.set(vSp.points[vp], vSp.points[vp + 1], 0.9);
            hMesh.userData = { type: 'vertexHandle', unitId: vUnit._id, vertexIndex: vp / 2 };
            this.threeScene.add(hMesh);
            this.overlayMeshes.push(hMesh);
            // Inner dot
            const hGeom2 = new THREE.CircleGeometry(handleRadius * 0.5, 16);
            const hMat2 = new THREE.MeshBasicMaterial({ color: '#4cc9f0' });
            const hMesh2 = new THREE.Mesh(hGeom2, hMat2);
            hMesh2.position.set(vSp.points[vp], vSp.points[vp + 1], 0.91);
            this.threeScene.add(hMesh2);
            this.overlayMeshes.push(hMesh2);
          }
        }
      }

      // Selection marquee
      if (this.selectionRectStart && this.selectionRectEnd) {
        const marqueePoints = this.buildRectPoints(this.selectionRectStart, this.selectionRectEnd);
        this.renderDraftShape(marqueePoints, '#f77f00', 0.15);
      }
    },

    renderDraftShape(points, color, opacity) {
      const shape = this.getShapeFromPoints(points);
      const geom = new THREE.ShapeGeometry(shape);
      const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.z = 0.75;
      this.threeScene.add(mesh);
      this.overlayMeshes.push(mesh);

      // Border
      const borderPts = [];
      const shapePts = shape.getPoints();
      for (let i = 0; i < shapePts.length; i++) {
        borderPts.push(new THREE.Vector3(shapePts[i].x, shapePts[i].y, 0.8));
      }
      borderPts.push(borderPts[0].clone());
      const borderGeom = new THREE.BufferGeometry().setFromPoints(borderPts);
      const borderMat = new THREE.LineBasicMaterial({ color });
      const borderLine = new THREE.Line(borderGeom, borderMat);
      this.threeScene.add(borderLine);
      this.overlayMeshes.push(borderLine);
    },

    getVertexDotRadius() {
      if (!this.threeCamera) return 1;
      // Visible world width = (right - left) / zoom; scale handle to ~1/200 of it.
      const zoom = this.threeCamera.zoom || 1;
      return (this.threeCamera.right - this.threeCamera.left) / zoom / 200;
    },

    hitTestVertexHandle(clientX, clientY) {
      if (!this.isEditMode || !this.selectedUnitIds.length || this.activeTool !== 'select') return null;
      const world = this.clientToWorld(clientX, clientY);
      const threshold = this.getVertexDotRadius() * 3;
      for (var i = 0; i < this.selectedUnitIds.length; i++) {
        const unit = this.units.find(u => u._id === this.selectedUnitIds[i]);
        if (!unit) continue;
        const sp = this.getNormalizedSitePlan(unit);
        if (!sp || sp.points.length < 6) continue;
        for (let p = 0; p < sp.points.length; p += 2) {
          const dx = world.x - sp.points[p];
          const dy = world.y - sp.points[p + 1];
          if (Math.sqrt(dx * dx + dy * dy) <= threshold) {
            return { unitId: unit._id, vertexIndex: p / 2 };
          }
        }
      }
      return null;
    },

    hitTestPolygonFirstVertex(clientX, clientY) {
      if (this.polygonPoints.length < 3) return false;
      const world = this.clientToWorld(clientX, clientY);
      const first = this.polygonPoints[0];
      const threshold = this.getVertexDotRadius() * 4;
      const dx = world.x - first.x;
      const dy = world.y - first.y;
      return Math.sqrt(dx * dx + dy * dy) <= threshold;
    },

    // ── Controls ─────────────────────────────────────────────────────

    teardownControls() {
      const canvas = this.$refs.canvas;
      if (!canvas || !this.controlHandlers) return;
      canvas.removeEventListener('wheel', this.controlHandlers.wheel);
      canvas.removeEventListener('mousedown', this.controlHandlers.mousedown);
      canvas.removeEventListener('mousemove', this.controlHandlers.mousemove);
      canvas.removeEventListener('mouseup', this.controlHandlers.mouseup);
      canvas.removeEventListener('mouseleave', this.controlHandlers.mouseleave);
      canvas.removeEventListener('dblclick', this.controlHandlers.dblclick);
      canvas.removeEventListener('touchstart', this.controlHandlers.touchstart);
      canvas.removeEventListener('touchmove', this.controlHandlers.touchmove);
      canvas.removeEventListener('touchend', this.controlHandlers.touchend);
      window.removeEventListener('keydown', this.controlHandlers.keydown);
      this.controlHandlers = null;
    },

    setupControls() {
      this.teardownControls();
      const canvas = this.$refs.canvas;
      if (!canvas) return;

      const self = this;
      this.controlHandlers = {
        wheel(e) {
          e.preventDefault();
          // Scroll up (deltaY < 0) → zoom in; scroll down → zoom out.
          const factor = e.deltaY > 0 ? (1 / 1.1) : 1.1;
          self.applyZoom(factor);
          self.threeRenderer.render(self.threeScene, self.threeCamera);
        },
        mousedown(e) { self.onMouseDown(e); },
        mousemove(e) { self.onMouseMove(e); },
        mouseup(e) { self.onMouseUp(e); },
        mouseleave(e) { self.onMouseUp(e); },
        dblclick(e) { self.onDoubleClick(e); },
        touchstart(e) { self.onTouchStart(e); },
        touchmove(e) { self.onTouchMove(e); },
        touchend(e) { self.onTouchEnd(e); },
        keydown(e) { self.onKeyDown(e); },
      };

      canvas.addEventListener('wheel', this.controlHandlers.wheel, { passive: false });
      canvas.addEventListener('mousedown', this.controlHandlers.mousedown);
      canvas.addEventListener('mousemove', this.controlHandlers.mousemove);
      canvas.addEventListener('mouseup', this.controlHandlers.mouseup);
      canvas.addEventListener('mouseleave', this.controlHandlers.mouseleave);
      canvas.addEventListener('dblclick', this.controlHandlers.dblclick);
      canvas.addEventListener('touchstart', this.controlHandlers.touchstart, { passive: true });
      canvas.addEventListener('touchmove', this.controlHandlers.touchmove, { passive: false });
      canvas.addEventListener('touchend', this.controlHandlers.touchend);
      window.addEventListener('keydown', this.controlHandlers.keydown);
    },

    // ── Mouse events ─────────────────────────────────────────────────

    onMouseDown(e) {
      const canvas = this.$refs.canvas;
      // Right-click or pan tool: always pan
      if (e.button === 2 || this.activeTool === 'pan') {
        this.isPanning = true;
        this.panStart = { x: e.clientX, y: e.clientY };
        if (canvas) canvas.style.cursor = 'grabbing';
        return;
      }

      if (e.button !== 0) return;
      const world = this.clientToWorld(e.clientX, e.clientY);

      if (this.activeTool === 'select') {
        // Check vertex handle hit first (for reshape)
        const vertexHit = this.hitTestVertexHandle(e.clientX, e.clientY);
        if (this.isEditMode && vertexHit) {
          this.isDraggingVertex = true;
          this.dragVertexUnitId = vertexHit.unitId;
          this.dragVertexIndex = vertexHit.vertexIndex;
          const vUnit = this.units.find(u => u._id === vertexHit.unitId);
          this.dragVertexSnapshot = this.getNormalizedSitePlan(vUnit);
          this.dragStartClient = { x: e.clientX, y: e.clientY };
          if (canvas) canvas.style.cursor = 'crosshair';
          return;
        }

        const hit = this.raycastUnits(e.clientX, e.clientY);
        const additive = !!(e.metaKey || e.ctrlKey);

        if (this.isEditMode && hit && this.selectedUnitIds.indexOf(hit) > -1 && !additive) {
          this.isDraggingUnits = true;
          this.hasDraggedUnits = false;
          this.dragStartClient = { x: e.clientX, y: e.clientY };
          this.dragStartWorld = world;
          this.dragUnitSnapshot = this.buildDragUnitSnapshot(this.selectedUnitIds);
          if (canvas) canvas.style.cursor = 'move';
          return;
        }

        this.selectGesture = {
          clientX: e.clientX,
          clientY: e.clientY,
          worldStart: world,
          hitUnitId: hit,
          additive,
        };
      } else if (this.activeTool === 'rect') {
        this.drawStart = world;
        this.draftRectPoints = [];
      } else if (this.activeTool === 'polygon') {
        this.drawStart = { clientX: e.clientX, clientY: e.clientY };
      }
    },

    onMouseMove(e) {
      // Panning
      if (this.isPanning) {
        const dx = e.clientX - this.panStart.x;
        const dy = e.clientY - this.panStart.y;
        this.applyPan(dx, dy);
        this.panStart = { x: e.clientX, y: e.clientY };
        this.threeRenderer.render(this.threeScene, this.threeCamera);
        return;
      }

      // Vertex drag (reshape)
      if (this.isDraggingVertex) {
        const vtxWorld = this.clientToWorld(e.clientX, e.clientY);
        const vUnit = this.units.find(u => u._id === this.dragVertexUnitId);
        if (vUnit) {
          const sp = this.getNormalizedSitePlan(vUnit);
          if (!sp) return;
          const vi = this.dragVertexIndex * 2;
          sp.points[vi] = vtxWorld.x;
          sp.points[vi + 1] = vtxWorld.y;
          sp.labelAnchor = this.getLabelAnchorFromPoints(sp.points);
          this.applySitePlanToLocalUnit(this.dragVertexUnitId, sp);
          this.renderUnitOverlays();
          this.renderInteractionOverlay();
          this.threeRenderer.render(this.threeScene, this.threeCamera);
        }
        return;
      }

      // Drag-move
      if (this.isDraggingUnits) {
        var movedPx = Math.hypot(
          e.clientX - this.dragStartClient.x,
          e.clientY - this.dragStartClient.y,
        );
        if (movedPx < 4) return;
        this.hasDraggedUnits = true;
        const dragWorld = this.clientToWorld(e.clientX, e.clientY);
        const ddx = dragWorld.x - this.dragStartWorld.x;
        const ddy = dragWorld.y - this.dragStartWorld.y;
        this.applyDragDelta(ddx, ddy);
        this.renderUnitOverlays();
        this.renderInteractionOverlay();
        this.threeRenderer.render(this.threeScene, this.threeCamera);
        return;
      }

      const world = this.clientToWorld(e.clientX, e.clientY);

      if (this.activeTool === 'select' && this.selectGesture && !this.selectGesture.hitUnitId) {
        var movedPx = Math.hypot(
          e.clientX - this.selectGesture.clientX,
          e.clientY - this.selectGesture.clientY,
        );
        if (movedPx >= 4) {
          this.selectionRectStart = this.selectGesture.worldStart;
          this.selectionRectEnd = world;
          this.renderInteractionOverlay();
          this.threeRenderer.render(this.threeScene, this.threeCamera);
        }
      } else if (this.activeTool === 'rect' && this.drawStart) {
        this.draftRectPoints = this.buildRectPoints(this.drawStart, world);
        this.renderInteractionOverlay();
        this.threeRenderer.render(this.threeScene, this.threeCamera);
      } else if (this.activeTool === 'polygon' && this.polygonPoints.length > 0) {
        this.polygonPreviewPoint = world;
        this.renderInteractionOverlay();
        this.threeRenderer.render(this.threeScene, this.threeCamera);
      }

      // Hover cursor feedback (only when not actively dragging/drawing)
      if (this.activeTool === 'select' && !this.selectGesture && !this.isDraggingUnits && !this.isDraggingVertex) {
        const canvas = this.$refs.canvas;
        if (canvas) {
          // Check vertex handles first
          if (this.isEditMode && this.hitTestVertexHandle(e.clientX, e.clientY)) {
            canvas.style.cursor = 'crosshair';
          } else {
            const hoverHit = this.raycastUnits(e.clientX, e.clientY);
            if (hoverHit && this.selectedUnitIds.indexOf(hoverHit) > -1 && this.isEditMode) {
              canvas.style.cursor = 'move';
            } else if (hoverHit) {
              canvas.style.cursor = 'pointer';
            } else {
              canvas.style.cursor = 'default';
            }
          }
        }
      }
    },

    async onMouseUp(e) {
      const canvas = this.$refs.canvas;
      if (this.isPanning) {
        this.isPanning = false;
        if (canvas) canvas.style.cursor = this.getCursorForTool();
        return;
      }

      if (this.isDraggingVertex) {
        const vtxUnitId = this.dragVertexUnitId;
        this.isDraggingVertex = false;
        this.dragVertexUnitId = null;
        this.dragVertexIndex = null;
        this.dragVertexSnapshot = null;
        this.dragStartClient = null;
        if (canvas) canvas.style.cursor = this.getCursorForTool();
        this.markDirty(vtxUnitId);
        return;
      }

      if (this.isDraggingUnits) {
        const moved = this.hasDraggedUnits;
        this.resetDragState();
        if (canvas) canvas.style.cursor = this.getCursorForTool();
        if (moved) {
          for (let di = 0; di < this.selectedUnitIds.length; di++) {
            this.markDirty(this.selectedUnitIds[di]);
          }
        }
        this.drawStart = null;
        return;
      }

      if (this.activeTool === 'select' && this.selectGesture) {
        if (this.selectionRectStart && this.selectionRectEnd) {
          this.finishRectSelection(this.selectGesture.additive);
        } else if (this.selectGesture.hitUnitId) {
          if (this.selectGesture.additive) {
            this.toggleUnitSelection(this.selectGesture.hitUnitId);
          } else {
            this.selectUnitIds([this.selectGesture.hitUnitId]);
          }
          const container = this.$refs.sceneContainer;
          if (container) {
            const rect = container.getBoundingClientRect();
            this.unitClickPosition = { x: e.clientX - rect.left, y: e.clientY - rect.top };
          }
        } else {
          this.clearSelection();
        }
        this.selectGesture = null;
      } else if (this.activeTool === 'rect' && this.drawStart) {
        this.finishRectDraft();
        this.drawStart = null;
      } else if (this.activeTool === 'polygon' && this.drawStart) {
        if (this.hitTestPolygonFirstVertex(e.clientX, e.clientY)) {
          this.finishPolygonDraft();
        } else {
          const world = this.clientToWorld(e.clientX, e.clientY);
          this.polygonPoints.push(world);
          this.polygonPreviewPoint = null;
          this.renderInteractionOverlay();
          this.threeRenderer.render(this.threeScene, this.threeCamera);
        }
        this.drawStart = null;
      }
    },

    onDoubleClick(e) {
      if (this.activeTool === 'polygon' && this.polygonPoints.length >= 3) {
        this.finishPolygonDraft();
      }
    },

    // ── Touch events ─────────────────────────────────────────────────

    onTouchStart(e) {
      if (e.touches.length === 1) {
        if (this.activeTool === 'rect') {
          this.drawStart = this.clientToWorld(e.touches[0].clientX, e.touches[0].clientY);
        } else if (this.activeTool === 'polygon') {
          if (this.hitTestPolygonFirstVertex(e.touches[0].clientX, e.touches[0].clientY)) {
            this.finishPolygonDraft();
          } else {
            var world = this.clientToWorld(e.touches[0].clientX, e.touches[0].clientY);
            this.polygonPoints.push(world);
            this.renderInteractionOverlay();
            this.threeRenderer.render(this.threeScene, this.threeCamera);
          }
        } else {
          // Single touch: vertex-resize / drag-move / select / marquee
          const cx = e.touches[0].clientX;
          const cy = e.touches[0].clientY;
          var world = this.clientToWorld(cx, cy);

          // Vertex handle hit → reshape
          const vertexHit = this.isEditMode ? this.hitTestVertexHandle(cx, cy) : null;
          if (vertexHit) {
            this.isDraggingVertex = true;
            this.dragVertexUnitId = vertexHit.unitId;
            this.dragVertexIndex = vertexHit.vertexIndex;
            const vUnit = this.units.find(u => u._id === vertexHit.unitId);
            this.dragVertexSnapshot = this.getNormalizedSitePlan(vUnit);
            this.dragStartClient = { x: cx, y: cy };
            return;
          }

          const hit = this.raycastUnits(cx, cy);

          if (this.isEditMode && hit && this.selectedUnitIds.indexOf(hit) > -1) {
            // Touch on an already-selected unit in edit mode → drag-move
            this.isDraggingUnits = true;
            this.hasDraggedUnits = false;
            this.dragStartClient = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            this.dragStartWorld = world;
            this.dragUnitSnapshot = this.buildDragUnitSnapshot(this.selectedUnitIds);
          } else {
            this.touchSelectGesture = {
              clientX: e.touches[0].clientX,
              clientY: e.touches[0].clientY,
              worldStart: world,
              hitUnitId: hit,
            };
          }
        }
      } else if (e.touches.length === 2) {
        // Two-finger: cancel any single-touch gesture in flight
        this.touchSelectGesture = null;
        this.selectionRectStart = null;
        this.selectionRectEnd = null;
        if (this.isDraggingVertex) {
          this.isDraggingVertex = false;
          this.dragVertexUnitId = null;
          this.dragVertexIndex = null;
          this.dragVertexSnapshot = null;
          this.dragStartClient = null;
        }
        if (this.isDraggingUnits) {
          this.resetDragState();
        }
        this.isPanning = true;
        const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        this.panStart = { x: mx, y: my };
        this.lastTouchDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY,
        );
      }
    },

    onTouchMove(e) {
      e.preventDefault();
      if (e.touches.length === 1) {
        // Vertex drag (reshape)
        if (this.isDraggingVertex) {
          const vtxWorld = this.clientToWorld(e.touches[0].clientX, e.touches[0].clientY);
          const vUnit = this.units.find(u => u._id === this.dragVertexUnitId);
          if (vUnit) {
            const sp = this.getNormalizedSitePlan(vUnit);
            if (!sp) return;
            const vi = this.dragVertexIndex * 2;
            sp.points[vi] = vtxWorld.x;
            sp.points[vi + 1] = vtxWorld.y;
            sp.labelAnchor = this.getLabelAnchorFromPoints(sp.points);
            this.applySitePlanToLocalUnit(this.dragVertexUnitId, sp);
            this.renderUnitOverlays();
            this.renderInteractionOverlay();
            this.threeRenderer.render(this.threeScene, this.threeCamera);
          }
          return;
        }
        // Drag-move selected units
        if (this.isDraggingUnits) {
          var movedPx = Math.hypot(
            e.touches[0].clientX - this.dragStartClient.x,
            e.touches[0].clientY - this.dragStartClient.y,
          );
          if (movedPx >= 4) {
            this.hasDraggedUnits = true;
            const dragWorld = this.clientToWorld(e.touches[0].clientX, e.touches[0].clientY);
            const ddx = dragWorld.x - this.dragStartWorld.x;
            const ddy = dragWorld.y - this.dragStartWorld.y;
            this.applyDragDelta(ddx, ddy);
            this.renderUnitOverlays();
            this.renderInteractionOverlay();
            this.threeRenderer.render(this.threeScene, this.threeCamera);
          }
          return;
        }
        if (this.touchSelectGesture && !this.touchSelectGesture.hitUnitId) {
          // Single-finger drag on empty space → marquee selection
          var movedPx = Math.hypot(
            e.touches[0].clientX - this.touchSelectGesture.clientX,
            e.touches[0].clientY - this.touchSelectGesture.clientY,
          );
          if (movedPx >= 4) {
            var world = this.clientToWorld(e.touches[0].clientX, e.touches[0].clientY);
            this.selectionRectStart = this.touchSelectGesture.worldStart;
            this.selectionRectEnd = world;
            this.renderInteractionOverlay();
            this.threeRenderer.render(this.threeScene, this.threeCamera);
          }
        } else if (this.activeTool === 'rect' && this.drawStart) {
          var world = this.clientToWorld(e.touches[0].clientX, e.touches[0].clientY);
          this.draftRectPoints = this.buildRectPoints(this.drawStart, world);
          this.renderInteractionOverlay();
          this.threeRenderer.render(this.threeScene, this.threeCamera);
        }
      } else if (e.touches.length === 2) {
        // Two-finger: pan + pinch-to-zoom
        const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        if (this.isPanning) {
          const dx = mx - this.panStart.x;
          const dy = my - this.panStart.y;
          this.applyPan(dx, dy);
          this.panStart = { x: mx, y: my };
        }
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY,
        );
        if (this.lastTouchDist && dist) {
          // Spreading fingers (dist > lastTouchDist) → zoom in.
          this.applyZoom(dist / this.lastTouchDist);
        }
        this.lastTouchDist = dist;
      }
      this.threeRenderer.render(this.threeScene, this.threeCamera);
    },

    onTouchEnd(e) {
      // Finish vertex drag (reshape)
      if (this.isDraggingVertex) {
        const vtxUnitId = this.dragVertexUnitId;
        this.isDraggingVertex = false;
        this.dragVertexUnitId = null;
        this.dragVertexIndex = null;
        this.dragVertexSnapshot = null;
        this.dragStartClient = null;
        this.markDirty(vtxUnitId);
      }
      // Finish drag-move
      if (this.isDraggingUnits) {
        const moved = this.hasDraggedUnits;
        this.resetDragState();
        if (moved) {
          for (let di = 0; di < this.selectedUnitIds.length; di++) {
            this.markDirty(this.selectedUnitIds[di]);
          }
        }
      }
      // Finish single-touch gestures
      if (this.touchSelectGesture) {
        if (this.selectionRectStart && this.selectionRectEnd) {
          this.finishRectSelection(false);
        } else if (this.touchSelectGesture.hitUnitId) {
          this.selectUnitIds([this.touchSelectGesture.hitUnitId]);
          const container = this.$refs.sceneContainer;
          if (container) {
            const rect = container.getBoundingClientRect();
            this.unitClickPosition = {
              x: this.touchSelectGesture.clientX - rect.left,
              y: this.touchSelectGesture.clientY - rect.top,
            };
          }
        } else {
          this.clearSelection();
        }
        this.touchSelectGesture = null;
      }
      if (this.activeTool === 'rect' && this.drawStart) {
        this.finishRectDraft();
        this.drawStart = null;
      }
      this.isPanning = false;
    },

    // ── Keyboard events ──────────────────────────────────────────────

    onKeyDown(e) {
      if (this.assignDialogVisible || this.renameDialogVisible) return;

      if (e.key === 'Escape') {
        if (this.activeTool === 'polygon' && this.polygonPoints.length > 0) {
          this.cancelInteraction();
        } else {
          this.clearSelection();
        }
      } else if (e.key === 'Enter' && this.activeTool === 'polygon' && this.polygonPoints.length >= 3) {
        this.finishPolygonDraft();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (this.isEditMode && this.canDelete && document.activeElement === document.body) {
          this.deleteSelectedSitePlans();
        }
      }
    },

    // ── Selection ────────────────────────────────────────────────────

    selectUnitIds(ids) {
      this.selectedUnitIds = ids.slice();
      this.syncSelectedUnit();
      this.renderUnitOverlays();
      this.renderInteractionOverlay();
      this.threeRenderer.render(this.threeScene, this.threeCamera);
    },

    toggleUnitSelection(unitId) {
      const idx = this.selectedUnitIds.indexOf(unitId);
      if (idx > -1) {
        this.selectedUnitIds.splice(idx, 1);
      } else {
        this.selectedUnitIds.push(unitId);
      }
      this.syncSelectedUnit();
      this.renderUnitOverlays();
      this.renderInteractionOverlay();
      this.threeRenderer.render(this.threeScene, this.threeCamera);
    },

    clearSelection() {
      this.selectedUnitIds = [];
      this.selectedUnit = null;
      this.unitClickPosition = null;
      this.renderUnitOverlays();
      this.renderInteractionOverlay();
      this.threeRenderer.render(this.threeScene, this.threeCamera);
    },

    syncSelectedUnit() {
      if (this.selectedUnitIds.length === 1) {
        const unitId = this.selectedUnitIds[0];
        const unit = this.units.find(u => u._id === unitId);
        if (!unit) {
          this.selectedUnit = null;
          return;
        }
        this.selectedUnit = Object.assign({}, unit);
        // Attach rental info
        if (unit.invoice && unit.customer) {
          const rental = this.unitRentals.find(r => r.unitInvoice === unit.invoice._id && r.customer === unit.customer);
          if (rental) {
            this.selectedUnit.rental = rental;
          }
        }
      } else {
        this.selectedUnit = null;
      }
    },

    raycastUnits(clientX, clientY) {
      if (!this.threeCamera || !this.threeRenderer) return null;
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      const mouse = new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, this.threeCamera);
      const targets = this.unitMeshes.filter(m => m.userData.type === 'unit');
      const intersects = raycaster.intersectObjects(targets);
      if (intersects.length > 0) {
        return intersects[0].object.userData.unitId;
      }
      return null;
    },

    navigateToUnit() {
      if (this.selectedUnit) {
        this.$router.push({ name: 'Facility Unit', params: { facility: this.facility, unit: this.selectedUnit._id } });
      }
    },

    // ── Drawing tool completions ─────────────────────────────────────

    finishRectDraft() {
      if (this.draftRectPoints.length !== 8) {
        this.draftRectPoints = [];
        this.renderInteractionOverlay();
        this.threeRenderer.render(this.threeScene, this.threeCamera);
        return;
      }
      // Check minimum size
      const bounds = this.getBoundsFromPoints(this.draftRectPoints);
      const w = bounds.maxX - bounds.minX;
      const h = bounds.maxY - bounds.minY;
      if (w < 1 || h < 1) {
        this.draftRectPoints = [];
        this.renderInteractionOverlay();
        this.threeRenderer.render(this.threeScene, this.threeCamera);
        return;
      }

      this.pendingAssignment = {
        source: 'draw',
        geometryType: 'rect',
        points: this.draftRectPoints.slice(),
        labelAnchor: this.getLabelAnchorFromPoints(this.draftRectPoints),
        rotation: 0,
        hidden: false,
      };
      this.assignForm.unitId = '';
      this.assignDialogVisible = true;
    },

    finishPolygonDraft() {
      if (this.polygonPoints.length < 3) return;

      const flatPoints = [];
      for (let i = 0; i < this.polygonPoints.length; i++) {
        flatPoints.push(this.polygonPoints[i].x, this.polygonPoints[i].y);
      }

      this.pendingAssignment = {
        source: 'draw',
        geometryType: 'polygon',
        points: flatPoints,
        labelAnchor: this.getLabelAnchorFromPoints(flatPoints),
        rotation: 0,
        hidden: false,
      };
      this.polygonPoints = [];
      this.polygonPreviewPoint = null;
      this.assignForm.unitId = '';
      this.assignDialogVisible = true;
    },

    finishRectSelection(additive) {
      if (!this.selectionRectStart || !this.selectionRectEnd) {
        this.selectionRectStart = null;
        this.selectionRectEnd = null;
        return;
      }

      const marqueePoints = this.buildRectPoints(this.selectionRectStart, this.selectionRectEnd);
      const marqueeBounds = this.getBoundsFromPoints(marqueePoints);

      // Check minimum marquee size
      const mw = marqueeBounds.maxX - marqueeBounds.minX;
      const mh = marqueeBounds.maxY - marqueeBounds.minY;
      if (mw < 0.5 && mh < 0.5) {
        this.selectionRectStart = null;
        this.selectionRectEnd = null;
        this.renderInteractionOverlay();
        this.threeRenderer.render(this.threeScene, this.threeCamera);
        return;
      }

      let matchedIds = [];
      for (let i = 0; i < this.units.length; i++) {
        const unit = this.units[i];
        if (unit.sitePlan && unit.sitePlan.hidden) continue;
        const sp = this.getNormalizedSitePlan(unit);
        if (!sp || sp.points.length < 6) continue;
        const unitBounds = this.getBoundsFromPoints(sp.points);
        if (this.boundsIntersect(marqueeBounds, unitBounds)) {
          matchedIds.push(unit._id);
        }
      }

      if (additive) {
        // Add to existing selection
        const combined = this.selectedUnitIds.slice();
        for (let j = 0; j < matchedIds.length; j++) {
          if (combined.indexOf(matchedIds[j]) === -1) {
            combined.push(matchedIds[j]);
          }
        }
        matchedIds = combined;
      }

      this.selectionRectStart = null;
      this.selectionRectEnd = null;
      this.selectUnitIds(matchedIds);
    },

    cancelInteraction() {
      this.drawStart = null;
      this.draftRectPoints = [];
      this.polygonPoints = [];
      this.polygonPreviewPoint = null;
      this.selectionRectStart = null;
      this.selectionRectEnd = null;
      this.selectGesture = null;
      this.touchSelectGesture = null;
      if (this.threeScene) {
        this.renderInteractionOverlay();
        this.threeRenderer.render(this.threeScene, this.threeCamera);
      }
    },

    // ── Mode & Tool management ───────────────────────────────────────

    enterEditMode() {
      if (!this.hasAccess) return;
      this.editSnapshot = JSON.parse(JSON.stringify(this.units));
      this.dirtyUnitIds = [];
      this.uiMode = 'edit';
      this.cancelInteraction();
    },

    async cancelEditMode() {
      if (!this.hasAccess) return;
      this.uiMode = 'view';
      this.setTool('select');
      this.cancelInteraction();
      this.clearSelection();

      // Restore units from snapshot and re-fetch from server to ensure consistency
      if (this.editSnapshot) {
        this.units = this.editSnapshot;
        this.editSnapshot = null;
      }
      this.dirtyUnitIds = [];
      await this.getUnits();
      this.renderScene();
    },

    async saveEditMode() {
      if (!this.hasAccess) return;
      if (this.dirtyUnitIds.length) {
        try {
          await this.persistDirtyUnits();
          clearFacilityCache(this.facility);
          this.showCanvasToast('Changes saved', 'success');
        } catch (err) {
          this.showCanvasToast(`Failed to save: ${(err.response && err.response.data) || err.message}`, 'error');
          return;
        }
      } else {
        this.showCanvasToast('Changes saved', 'success');
      }
      this.uiMode = 'view';
      this.setTool('select');
      this.cancelInteraction();
      this.clearSelection();
      this.editSnapshot = null;
      this.dirtyUnitIds = [];
    },

    markDirty(unitId) {
      if (this.dirtyUnitIds.indexOf(unitId) === -1) {
        this.dirtyUnitIds.push(unitId);
      }
    },

    async persistDirtyUnits() {
      const self = this;
      if (this.dirtyUnitIds.length === 1) {
        const unitId = this.dirtyUnitIds[0];
        const unit = this.units.find(u => u._id === unitId);
        const sp = this.getNormalizedSitePlan(unit);
        if (!sp) return;
        await axios.patch(UNITS_API_V2_SITE_PLAN(unitId), {
          geometryType: sp.geometryType,
          points: sp.points,
          labelAnchor: sp.labelAnchor,
          rotation: sp.rotation || 0,
          hidden: !!sp.hidden,
        }, {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });
        return;
      }
      await axios.patch(UNITS_API_V2_BULK_SITE_PLAN(this.facility), {
        units: this.dirtyUnitIds.map((id) => {
          const unit = self.units.find(u => u._id === id);
          const sp = self.getNormalizedSitePlan(unit);
          return {
            unitId: id,
            geometryType: sp ? sp.geometryType : 'polygon',
            points: sp ? sp.points : [],
            labelAnchor: sp ? sp.labelAnchor : [],
            rotation: sp ? sp.rotation : 0,
            hidden: sp ? !!sp.hidden : true,
          };
        }),
      }, {
        headers: { Authorization: `Bearer ${this.$keycloak.token}` },
      });
    },

    setTool(tool) {
      if (this.activeTool !== tool) {
        this.cancelInteraction();
      }
      this.activeTool = tool;
      const canvas = this.$refs.canvas;
      if (canvas) {
        canvas.style.cursor = this.getCursorForTool();
      }
    },

    getCursorForTool() {
      if (this.activeTool === 'pan') return 'grab';
      if (this.activeTool === 'rect' || this.activeTool === 'polygon') return 'crosshair';
      return 'default';
    },

    // ── Edit actions ─────────────────────────────────────────────────

    copySelectedGeometry() {
      if (this.selectedUnitIds.length !== 1) return;
      const unitId = this.selectedUnitIds[0];
      const unit = this.units.find(u => u._id === unitId);
      if (!unit) return;

      const sp = this.getNormalizedSitePlan(unit);
      if (!sp) return;
      this.pendingAssignment = {
        source: 'copy',
        sourceUnitId: unit._id,
        geometryType: sp.geometryType,
        points: sp.points.slice(),
        labelAnchor: sp.labelAnchor.slice(),
        rotation: sp.rotation || 0,
        hidden: false,
      };
      this.assignForm.unitId = '';
      this.assignDialogVisible = true;
    },

    deleteSelectedSitePlans() {
      if (this.selectedUnitIds.length === 0) return;

      const count = this.selectedUnitIds.length;
      this.deleteConfirmMessage = count === 1
        ? 'Remove this unit\'s polygon from the site plan?'
        : `Remove ${count} unit polygons from the site plan?`;
      this.deleteConfirmVisible = true;
    },

    async executeDeleteSitePlans() {
      this.deleteConfirmVisible = false;

      const self = this;
      const count = this.selectedUnitIds.length;
      const payload = {
        units: this.selectedUnitIds.map((id) => {
          const unit = self.units.find(u => u._id === id);
          const sp = self.getNormalizedSitePlan(unit);
          return {
            unitId: id,
            geometryType: sp ? sp.geometryType : 'polygon',
            points: sp ? sp.points : [],
            labelAnchor: sp ? sp.labelAnchor : [],
            rotation: sp ? sp.rotation : 0,
            hidden: true,
          };
        }),
      };

      try {
        await axios.patch(UNITS_API_V2_BULK_SITE_PLAN(this.facility), payload, {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });

        clearFacilityCache(this.facility);

        // Mark each deleted unit's sitePlan as hidden in local state.
        // We replace the whole sitePlan object (instead of mutating .hidden in place)
        // so Vue 2 picks up the change and the assignable-units dropdown re-renders.
        for (let i = 0; i < this.selectedUnitIds.length; i++) {
          var deletedUnitId = this.selectedUnitIds[i];
          const unitIndex = this.units.findIndex(u => u._id === deletedUnitId);
          if (unitIndex === -1) continue;

          const existingUnit = this.units[unitIndex];
          const newSitePlan = Object.assign({}, existingUnit.sitePlan || {});
          newSitePlan.hidden = true;

          const updatedUnit = Object.assign({}, existingUnit);
          updatedUnit.sitePlan = newSitePlan;

          this.$set(this.units, unitIndex, updatedUnit);
        }

        this.showCanvasToast(count === 1 ? 'Polygon removed' : `${count} polygons removed`, 'success');
        this.clearSelection();
      } catch (err) {
        this.showCanvasToast(`Failed to delete: ${(err.response && err.response.data) || err.message}`, 'error');
      }
    },

    openRenameDialog() {
      if (this.selectedUnitIds.length !== 1) return;
      const unitId = this.selectedUnitIds[0];
      const unit = this.units.find(u => u._id === unitId);
      if (!unit) return;

      this.renameForm.sourceUnitId = unitId;
      this.renameForm.unitId = '';
      this.renameDialogVisible = true;
    },

    async confirmRenameDialog() {
      if (!this.renameForm.unitId || !this.renameForm.sourceUnitId) return;
      if (this.renameForm.unitId === this.renameForm.sourceUnitId) {
        this.showCanvasToast('Source and target are the same unit', 'info');
        return;
      }

      this.savingSitePlan = true;
      try {
        const sourceUnit = this.units.find(u => u._id === this.renameForm.sourceUnitId);
        const sp = this.getNormalizedSitePlan(sourceUnit);
        if (!sp) {
          this.showCanvasToast('Source unit has no site plan geometry', 'error');
          this.savingSitePlan = false;
          return;
        }

        // Assign geometry to target unit
        await axios.patch(UNITS_API_V2_SITE_PLAN(this.renameForm.unitId), {
          geometryType: sp.geometryType,
          points: sp.points,
          labelAnchor: sp.labelAnchor,
          rotation: sp.rotation || 0,
          hidden: false,
        }, {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });

        // Hide source unit's geometry
        await axios.patch(UNITS_API_V2_SITE_PLAN(this.renameForm.sourceUnitId), {
          geometryType: sp.geometryType,
          points: sp.points,
          labelAnchor: sp.labelAnchor,
          rotation: sp.rotation || 0,
          hidden: true,
        }, {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });

        clearFacilityCache(this.facility);

        // Update local state
        this.applySitePlanToLocalUnit(this.renameForm.unitId, {
          geometryType: sp.geometryType,
          points: sp.points,
          labelAnchor: sp.labelAnchor,
          rotation: sp.rotation || 0,
          hidden: false,
        });
        this.applySitePlanToLocalUnit(this.renameForm.sourceUnitId, {
          geometryType: sp.geometryType,
          points: sp.points,
          labelAnchor: sp.labelAnchor,
          rotation: sp.rotation || 0,
          hidden: true,
        });

        this.renameDialogVisible = false;
        this.showCanvasToast('Polygon reassigned', 'success');
        this.selectUnitIds([this.renameForm.unitId]);
      } catch (err) {
        this.showCanvasToast(`Failed to reassign: ${(err.response && err.response.data) || err.message}`, 'error');
      } finally {
        this.savingSitePlan = false;
      }
    },

    // ── Assign dialog ────────────────────────────────────────────────

    closeAssignDialog() {
      this.assignDialogVisible = false;
      this.pendingAssignment = null;
      this.draftRectPoints = [];
      this.renderInteractionOverlay();
      this.threeRenderer.render(this.threeScene, this.threeCamera);
    },

    async confirmAssignDialog() {
      if (!this.pendingAssignment || !this.assignForm.unitId) return;

      this.savingSitePlan = true;
      try {
        await axios.patch(UNITS_API_V2_SITE_PLAN(this.assignForm.unitId), {
          geometryType: this.pendingAssignment.geometryType,
          points: this.pendingAssignment.points,
          labelAnchor: this.pendingAssignment.labelAnchor,
          rotation: this.pendingAssignment.rotation || 0,
          hidden: false,
        }, {
          headers: { Authorization: `Bearer ${this.$keycloak.token}` },
        });

        clearFacilityCache(this.facility);

        this.applySitePlanToLocalUnit(this.assignForm.unitId, {
          geometryType: this.pendingAssignment.geometryType,
          points: this.pendingAssignment.points,
          labelAnchor: this.pendingAssignment.labelAnchor,
          rotation: this.pendingAssignment.rotation || 0,
          hidden: false,
        });

        this.assignDialogVisible = false;
        this.pendingAssignment = null;
        this.draftRectPoints = [];
        this.showCanvasToast('Geometry assigned', 'success');
        this.selectUnitIds([this.assignForm.unitId]);
      } catch (err) {
        this.showCanvasToast(`Failed to assign: ${(err.response && err.response.data) || err.message}`, 'error');
      } finally {
        this.savingSitePlan = false;
      }
    },

    applySitePlanToLocalUnit(unitId, sitePlanData) {
      // Replace the unit (and its sitePlan) immutably so Vue 2 reactivity
      // notices the change and re-renders the assignable-units dropdown.
      const unitIndex = this.units.findIndex(u => u._id === unitId);
      if (unitIndex === -1) return;

      const existingUnit = this.units[unitIndex];
      const mergedSitePlan = Object.assign({}, existingUnit.sitePlan || {}, sitePlanData);

      const updatedUnit = Object.assign({}, existingUnit);
      updatedUnit.sitePlan = mergedSitePlan;

      this.$set(this.units, unitIndex, updatedUnit);
    },

    hydrateUnitDetails(unit) {
      const result = Object.assign({}, unit);
      if (unit.invoice && unit.customer) {
        const rental = this.unitRentals.find(r => r.unitInvoice === unit.invoice._id && r.customer === unit.customer);
        if (rental) {
          result.rental = rental;
        }
      }
      result.computedPrice = this.facilityData
        ? computeUnitPricing(result, this.facilityData)
        : 0;
      return result;
    },

    // ── Drag-move ────────────────────────────────────────────────────

    buildDragUnitSnapshot(unitIds) {
      const snapshot = {};
      for (var i = 0; i < unitIds.length; i++) {
        const unit = this.units.find(u => u._id === unitIds[i]);
        if (!unit) continue;
        const sp = this.getNormalizedSitePlan(unit);
        if (!sp) continue;
        snapshot[unitIds[i]] = sp;
      }
      return snapshot;
    },

    translatePoints(points, dx, dy) {
      const translated = [];
      for (let i = 0; i < points.length; i += 2) {
        translated.push(points[i] + dx, points[i + 1] + dy);
      }
      return translated;
    },

    applyDragDelta(dx, dy) {
      const ids = this.selectedUnitIds.slice();
      for (let i = 0; i < ids.length; i++) {
        const unitId = ids[i];
        const base = this.dragUnitSnapshot[unitId];
        if (!base) continue;
        this.applySitePlanToLocalUnit(unitId, {
          geometryType: base.geometryType,
          points: this.translatePoints(base.points, dx, dy),
          labelAnchor: [base.labelAnchor[0] + dx, base.labelAnchor[1] + dy],
          rotation: base.rotation || 0,
          hidden: !!base.hidden,
        });
      }
    },

    resetDragState() {
      this.isDraggingUnits = false;
      this.hasDraggedUnits = false;
      this.dragStartClient = null;
      this.dragStartWorld = null;
      this.dragUnitSnapshot = null;
      this.isDraggingVertex = false;
      this.dragVertexUnitId = null;
      this.dragVertexIndex = null;
      this.dragVertexSnapshot = null;
    },

    snapshotCameraState() {
      if (!this.threeCamera) return null;
      return {
        left: this.threeCamera.left,
        right: this.threeCamera.right,
        top: this.threeCamera.top,
        bottom: this.threeCamera.bottom,
        zoom: this.threeCamera.zoom,
        position: this.threeCamera.position.clone(),
        quaternion: this.threeCamera.quaternion.clone(),
      };
    },

    restoreCameraState(snapshot) {
      if (!snapshot || !this.threeCamera) return;
      this.threeCamera.left = snapshot.left;
      this.threeCamera.right = snapshot.right;
      this.threeCamera.top = snapshot.top;
      this.threeCamera.bottom = snapshot.bottom;
      this.threeCamera.zoom = snapshot.zoom;
      this.threeCamera.position.copy(snapshot.position);
      this.threeCamera.quaternion.copy(snapshot.quaternion);
      this.threeCamera.updateProjectionMatrix();
    },

    captureSceneCanvas(targetAspect) {
      if (!this.threeRenderer || !this.threeScene || !this.threeCamera) return null;

      const renderer = this.threeRenderer;
      const maxEdge = 3600;
      const aspect = targetAspect || (297 / 210);
      let width = maxEdge;
      let height = Math.max(1, Math.round(width / aspect));
      if (height > maxEdge) {
        height = maxEdge;
        width = Math.max(1, Math.round(height * aspect));
      }

      const target = new THREE.WebGLRenderTarget(width, height, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
      });

      const pixels = new Uint8Array(width * height * 4);
      const prevTarget = renderer.getRenderTarget();
      const cameraSnapshot = this.snapshotCameraState();
      const prevClearColor = renderer.getClearColor(new THREE.Color()).clone();
      const prevClearAlpha = renderer.getClearAlpha();

      try {
        this.updateCameraToFit(width, height);
        this.threeCamera.updateProjectionMatrix();

        renderer.setClearColor(0xffffff, 1);
        renderer.setRenderTarget(target);
        this.renderScene();
        renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
      } finally {
        renderer.setRenderTarget(prevTarget);
        renderer.setClearColor(prevClearColor, prevClearAlpha);
        this.restoreCameraState(cameraSnapshot);
        this.renderScene();
        target.dispose();
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      const imageData = ctx.createImageData(width, height);

      for (let y = 0; y < height; y++) {
        const srcStart = (height - y - 1) * width * 4;
        const dstStart = y * width * 4;
        imageData.data.set(pixels.subarray(srcStart, srcStart + width * 4), dstStart);
      }

      ctx.putImageData(imageData, 0, 0);
      return canvas;
    },

    saveToPdf() {
      if (!this.threeRenderer) return;
      this.pdfLoading = true;
      try {
        const locationName = this.facilityData && this.facilityData.title
          ? String(this.facilityData.title).trim()
          : String(this.facility || '').trim();
        const pdfTitle = `ReadySpaces ${locationName || 'Facility'} Sitemap`;

        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4',
          compress: true,
        });

        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();

        const imageCanvas = this.captureSceneCanvas(pageW / pageH);
        if (!imageCanvas) throw new Error('Failed to capture scene');

        pdf.text(10, 10, pdfTitle);
        const now = new Date();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const year = now.getFullYear();
        let hours = now.getHours();
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours %= 12;
        hours = hours || 12;
        hours = String(hours).padStart(2, '0');
        const timestamp = `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
        pdf.setFontSize(10);
        pdf.text(pageW - 10, 10, timestamp, { align: 'right' });
        pdf.setFontSize(16);

        const imgW = imageCanvas.width;
        const imgH = imageCanvas.height;
        const maxW = pageW - 10;
        const maxH = pageH - 20;
        const scale = Math.min(maxW / imgW, maxH / imgH);
        const renderW = imgW * scale;
        const renderH = imgH * scale;

        const x = (pageW - renderW) / 2;
        const y = 15 + ((maxH - renderH) / 2);

        const imageUrl = imageCanvas.toDataURL('image/png');
        pdf.addImage(imageUrl, 'PNG', x, y, renderW, renderH);

        const safeFilename = pdfTitle.replace(/[<>:"/\\|?*\x00-\x1F]/g, '').replace(/\s+/g, ' ').trim();
        pdf.save(`${safeFilename || 'sitemap'}.pdf`);
        this.showCanvasToast('PDF downloaded', 'success');
      } catch (err) {
        this.showCanvasToast('Failed to generate PDF', 'error');
      } finally {
        this.pdfLoading = false;
      }
    },

    truncateText(text, maxLen) {
      if (!text) return '';
      if (text.length <= maxLen) return text;
      return `${text.substring(0, maxLen)}...`;
    },

    getPendingItemsSummary(unit) {
      const items = unit && unit.invoice && unit.invoice.pendingItems
        ? unit.invoice.pendingItems
        : [];
      if (!items.length) return 'READY';
      const labels = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i] || {};
        const key = String(item.key || '').toLowerCase();
        const reason = String(item.reason || '').toLowerCase();
        if ((key === 'id' || key === 'missingid' || key === 'govtid' || reason.indexOf('id') > -1) && labels.indexOf('ID') === -1) {
          labels.push('ID');
        } else if ((key === 'coi' || key === 'insurance' || reason.indexOf('insurance') > -1 || reason.indexOf('coi') > -1) && labels.indexOf('Insurance') === -1) {
          labels.push('Insurance');
        } else if ((key === 'agmt' || key === 'agreement' || reason.indexOf('agreement') > -1 || reason.indexOf('sign') > -1) && labels.indexOf('Agreement') === -1) {
          labels.push('Agreement');
        } else if ((key === 'payment' || reason.indexOf('payment') > -1 || reason.indexOf('deposit') > -1 || reason.indexOf('due') > -1) && labels.indexOf('Payment') === -1) {
          labels.push('Payment');
        }
      }
      return labels.length ? labels.join(', ') : 'READY';
    },

    showCanvasToast(message, type) {
      const icons = { success: 'el-icon-success', error: 'el-icon-error', info: 'el-icon-info' };
      const toast = { id: ++this.canvasToastId, message, type: type || 'info', icon: icons[type] || icons.info };
      this.canvasToasts.push(toast);
      setTimeout(() => {
        this.canvasToasts = this.canvasToasts.filter(t => t.id !== toast.id);
      }, 3000);
    },
    async toggleFullscreen() {
      const container = this.$refs.sceneContainer;
      if (!container) return;
      try {
        const isCurrent = document.fullscreenElement === container || document.webkitFullscreenElement === container;
        if (isCurrent) {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          }
        } else if (container.requestFullscreen) {
          await container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        }
      } catch (err) {
        this.showCanvasToast('Unable to toggle fullscreen', 'error');
      }
      // Camera resize is handled by the fullscreenchange listener
    },
    handleFullscreenChange() {
      const container = this.$refs.sceneContainer;
      this.isFullscreen = !!container && (
        document.fullscreenElement === container ||
        document.webkitFullscreenElement === container
      );
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.handleResize();
        });
      });
    },
    handleResize() {
      const container = this.$refs.sceneContainer;
      if (!container || !this.threeRenderer || !this.threeCamera) return;

      const rect = container.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      this.threeRenderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.threeRenderer.setSize(width, height);
      // Preserve the user's current pan/zoom across resize (e.g. fullscreen
      // toggle) instead of re-fitting to the full scene bounds.
      this.updateCameraForResize(width, height);

      this.renderScene();
    },
  },
};
</script>

<style scoped>
.scene-container {
  width: 100%;
  min-height: 400px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e4e4e7;
  background: #fff;
}
.scene-container:fullscreen,
.scene-container:-webkit-full-screen {
  width: 100vw;
  height: 100vh;
  min-height: 100vh;
  border-radius: 0;
  border: 0;
}
.scene-container canvas {
  display: block;
  width: 100%;
  height: 100%;
}
.import-banner {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  max-width: 500px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
}

/* ── In-canvas toast notifications ───────────────────────────────── */
/* ── Legend popover (below edit toolbar, top-right) ───────────────── */
.legend-popover-wrap {
  position: relative;
}
.legend-popover {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;
  padding: 1rem;
  border: solid 1px #eee;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  width: 25rem;
  z-index: 30;
}

.canvas-toast-container {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  pointer-events: none;
}
.canvas-toast {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
}
.canvas-toast--success {
  background: #f0f9eb;
  color: #67c23a;
  border: 1px solid #e1f3d8;
}
.canvas-toast--error {
  background: #fef0f0;
  color: #f56c6c;
  border: 1px solid #fde2e2;
}
.canvas-toast--info {
  background: #f4f4f5;
  color: #909399;
  border: 1px solid #e9e9eb;
}
.canvas-toast-enter-active {
  transition: all 0.3s ease;
}
.canvas-toast-leave-active {
  transition: all 0.3s ease;
}
.canvas-toast-enter,
.canvas-toast-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
.dxf-upload {
  display: inline-block;
}

/* ── Overlay layout ─────────────────────────────────────────────── */
.scene-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  pointer-events: none;
}
.scene-overlay-top {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.scene-overlay-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.scene-overlay-stack--right {
  align-items: flex-end;
}
.scene-overlay-bottom-right {
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

/* ── Panel base (shared by toolbar rows, help bar, side panels) ── */
.scene-overlay-panel,
.scene-help-bar,
.scene-side-panel {
  pointer-events: auto;
  background: rgba(255, 255, 255, 0.88);
  color: #18181b;
  border: 1px solid #e4e4e7;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.scene-overlay-panel {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  flex-wrap: wrap;
}

/* ── Help bar ────────────────────────────────────────────────────── */
.scene-help-bar {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  white-space: nowrap;
}

/* ── Side panels ─────────────────────────────────────────────────── */
.scene-side-panel {
  position: absolute;
  top: 80px;
  right: 12px;
  width: 300px;
  max-height: calc(100% - 24px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 10;
}
.scene-side-panel--multi {
  top: 12px;
}
.side-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px 10px;
  border-bottom: 1px solid #e4e4e7;
  flex-shrink: 0;
}
.side-panel-scroll {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.unit-info-name {
  font-size: 14px;
  font-weight: 600;
  color: #18181b;
}
.unit-info-body {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 10px 14px;
}
.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 6px 0;
  border-bottom: 1px solid #f4f4f5;
  font-size: 12px;
  color: #18181b;
}
.info-label {
  font-weight: 500;
  color: #71717a;
  margin-right: 8px;
  white-space: nowrap;
}
.unit-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.info-value--wrap {
  white-space: normal;
  word-break: break-word;
  text-align: right;
  max-width: 180px;
}

/* ── Multi-unit accordion titles ─────────────────────────────────── */
.multi-unit-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 8px;
  font-size: 13px;
}
.multi-unit-status {
  font-size: 11px;
  color: #71717a;
}

/* ── Button styling (shadcn light) ───────────────────────────────── */
.scene-overlay >>> .scene-btn.el-button {
  background: #fff;
  border: 1px solid #e4e4e7;
  color: #18181b;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1;
  font-weight: 500;
  transition: all 0.15s ease;
}
.scene-btn-icon {
  width: 14px;
  height: 14px;
  vertical-align: -2px;
  margin-right: 2px;
}
.scene-overlay >>> .scene-btn.el-button:hover,
.scene-overlay >>> .scene-btn.el-button:focus {
  background: #f4f4f5;
  border-color: #d4d4d8;
  color: #09090b;
}
.scene-overlay >>> .scene-btn.el-button.is-active {
  background: #009aca;
  border-color: #009aca;
  color: #fafafa;
}
.scene-overlay >>> .scene-btn.el-button.is-danger {
  color: #dc2626;
}
.scene-overlay >>> .scene-btn.el-button.is-danger:hover {
  background: #fef2f2;
  border-color: #fecaca;
}
.scene-overlay >>> .scene-btn.el-button.is-disabled,
.scene-overlay >>> .scene-btn.el-button.is-disabled:hover {
  opacity: 0.50;
  color: #a1a1aa;
  background: #fafafa;
  border-color: #e4e4e7;
}
.scene-side-panel >>> .scene-btn.el-button {
  background: #fff;
  border: 1px solid #e4e4e7;
  color: #18181b;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1;
  font-weight: 500;
}
.scene-side-panel >>> .scene-btn.el-button.is-active {
  background: #009aca;
  border-color: #009aca;
  color: #fafafa;
}

/* ── Close button in side panel ──────────────────────────────────── */
.side-panel-close.el-button--text {
  color: #a1a1aa;
  padding: 4px;
}
.side-panel-close.el-button--text:hover {
  color: #18181b;
}

/* ── Collapse styling for multi-select ───────────────────────────── */
.scene-side-panel >>> .el-collapse {
  border: 0;
  background: transparent;
}
.scene-side-panel >>> .el-collapse-item__wrap {
  background: transparent;
  border-bottom: 1px solid #f4f4f5;
}
.scene-side-panel >>> .el-collapse-item__header {
  background: transparent;
  color: #18181b;
  border-bottom: 1px solid #f4f4f5;
  padding: 0 14px;
  height: 40px;
  line-height: 40px;
  font-size: 13px;
}
.scene-side-panel >>> .el-collapse-item__header:hover {
  background: #fafafa;
}
.scene-side-panel >>> .el-collapse-item__content {
  color: #3f3f46;
  padding-bottom: 0;
  background: transparent;
}
.scene-side-panel >>> .el-collapse-item__arrow {
  color: #a1a1aa;
}

/* ── Tag styling in side panel ───────────────────────────────────── */
.scene-side-panel >>> .el-tag--mini {
  background: #f4f4f5;
  border-color: #e4e4e7;
  color: #3f3f46;
}

/* ── Toolbar label ────────────────────────────────────────────────── */
.scene-toolbar-label {
  font-size: 12px;
  font-weight: 500;
  color: #71717a;
  white-space: nowrap;
}

/* ── Collapsible help panel ──────────────────────────────────────── */
.scene-overlay-help {
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  max-width: 300px;
}
.scene-help-toggle {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
  padding: 7px 10px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #71717a;
  text-align: left;
}
.scene-help-toggle:hover {
  color: #18181b;
}
.scene-help-chevron {
  margin-left: auto;
  transition: transform 0.2s ease;
  font-size: 12px;
}
.scene-help-chevron.is-collapsed {
  transform: rotate(-90deg);
}
.scene-help-content {
  padding: 0 10px 8px;
}
.scene-help-item {
  font-size: 12px;
  color: #52525b;
  line-height: 1.6;
  padding: 2px 0;
}
.scene-help-item > i {
  color: #a1a1aa;
  margin-right: 4px;
}

.dialog-help-text {
  color: #909399;
  font-size: 13px;
  margin-top: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/FacilityScene.vue